// Service — the whole Service Smartsheet, made genuinely useful for the floor. Two ways to look at it:
//   • Board  — model/service GROUP → VESSEL card → WORKS/TASKS, with status balls, season tags, a
//     done/total roll-up and every field editable (inline or behind the fields panel).
//   • Sheet  — a flat, sortable table of every vessel with all the sheet's columns, real Smartsheet
//     fill colours and symbol balls — the faithful "everything at once" scan.
// Search, group / season / status filters and a "has open work" toggle sit above both; edits, adds and
// deletes write straight back to Smartsheet. Season tags come from the boat's build season.
//
// Evolved look: a group is a plain heading row (name, vessel count, the works' status roll-up), never a
// navy slab. A vessel card's header reads across: name over "SAP · model", the season tag, the boat's
// build journey, its works count, the roll-up as coloured counts and the done fraction.
// The journey strip is the boat's SERVICE journey (Expected → At the shipyard → In service → Ready →
// Delivered, lib/serviceJourney.ts): it follows the boat's @ Shipyard value, then its works' Status /
// Done, then its dates, so it always agrees with the sheet. A boat nothing is known about yet draws no
// strip. Only a sheet without an @ Shipyard column falls back to the build journey: the vessel matched by
// SAP to the current season's overview fleet (a boat not in it shown as delivered). The fleet match also
// supplies the model under the name.
// Beside the name, the vessel's Entry form and Washing buttons: Washing says where the boat's wash stands on
// the Washing Plan (Washing Not Scheduled, Washing Scheduled, No Wash Needed or Washed) and opens that wash
// (components/WashDialog.tsx), to plan it, tick it, mark it as needing no wash or note it, without leaving
// the page.
import { Fragment, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type HTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { findServiceColumn, findServiceStatusColumn, journeyOf, JOURNEY_STAGES, SERVICE_EXTRA_COLUMNS, SERVICE_PENDING_COLUMN_ID, seasonIdFor, serviceExtraKindOf, washId, type JourneyStep, type OverviewVessel, type ServiceExtraKind } from '@technohull/shared';
import type { BoatDocRecord, ServicePhotoSource } from '@technohull/shared';
import {
  useBoatDocs,
  useServiceAddRow,
  useServiceDeleteRow,
  useServiceEnsureColumn,
  useServiceNewVisit,
  useServiceSheet,
  useServiceWrite,
  useServiceWriteCells,
  type OverviewResponse,
  type ServiceCellValue,
  type SheetGridColumn,
  type SheetGridRow,
} from '../features/hooks.js';
import { api } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { ServiceGantt, type GanttVessel } from './ServiceGantt.js';
import { useSeason } from '../season/SeasonContext.js';
import { useCoopView } from '../collab/CollabContext.js';
import { ActionBar, Chip, ChipGroup, EmptyState, ErrorState, FilterBar, FilterSelect, FilterToggle, InlineError, LoadingState, LogoSpin, OverflowActions, PageHeader, SearchInput, Segmented, useConfirm } from '../components/ui.js';
import { IconCamera, IconChat, IconChevronRight, IconClose, IconDownload, IconFlag, IconMore, IconPaperclip, IconPrinter, IconRefresh, IconTool, IconTrash } from '../components/icons.js';
import { ServicePhotos, dragHasFiles } from '../components/ServicePhotos.js';
import { useHoverTip } from '../components/HoverTip.js';
import { serviceVesselDateFields } from '../features/serviceDates.js';
import { isCurrentServiceSeason, serviceSeasonColumns, serviceVesselSeason, serviceVisitDates } from '../features/serviceSeason.js';
import { serviceVisits } from '../features/serviceVisits.js';
import { ServiceVisitsDialog, type VisitLine } from '../components/ServiceVisits.js';
import { serviceStatusCloses, serviceStatusLabel, serviceStatusOf, serviceStatusShort, serviceStatusText } from '../features/serviceStatus.js';
import { CONTROL_FORM_NAME, IconEntryForm, PRINT_BLOCKED, VesselEntryForm } from '../components/VesselEntryForm.js';
import { IconDrop, IconDropDone, IconDropOff, WashDialog, type WashEntry } from '../components/WashDialog.js';
import { serviceWashVessels, serviceWashes, useSetWash, useWashes } from '../features/washPlan.js';
import { formToOpen, formsByVessel, isClosed, isControlForm, serviceVesselDates, type EntryVessel } from '../features/vesselEntry.js';
import { printBoatDoc } from '../features/boatDocPrint.js';
import { printVesselEntry } from '../features/vesselEntryPrint.js';
import { readDocLang } from '../features/boatDocCopy.js';
import { JourneyStrip, journeySummary } from '../components/JourneyStrip.js';
import { serviceJourney, serviceStepWord, yardStateOf, type ServiceJourney } from '../lib/serviceJourney.js';
import { SortMark } from '../components/SortMark.js';
import { useCellHistoryHover } from '../components/CellHistory.js';
import { NameField } from '../components/NameField.js';
import { DateField } from '../components/DateField.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';
import { useDateFormatter } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { fold, hasText } from '../lib/search.js';
import { sortRows, useTableSort } from '../lib/tableSort.js';
import { printSchedule } from '../features/exportSchedule.js';
import { parseVesselParam, revealVesselCard, VESSEL_PARAM, vesselLinkStep, vesselOfRow, withoutVesselParam } from '../features/serviceLink.js';
import { compareSeasonIds, normalizeSeasonId, type RowSeason } from '../features/seasonTag.js';

const cellStr = (v: string | boolean | undefined): string => (typeof v === 'string' ? v : v == null ? '' : v ? '✓' : '');
const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
const INLINE_TYPES = new Set(['CHECKBOX', 'DATE', 'DATETIME', 'PICKLIST']);
// a task counts as "done" when a completion checkbox is ticked or its status reads like finished work.
const DONE_RE = /complete|done|closed|✔|✓|delivered|ready|ολοκλ|τελείωσ|έτοιμ/i;
// free-text columns that hold line breaks (Notes and the like). A text <input> strips the breaks from its
// value, so a focus + blur would write the flattened text back to Smartsheet; these get a growing
// <textarea> instead, and every text editor only writes when the text really changed.
const MULTILINE_TITLE = /note|comment|remark|σημείωσ|σχόλ|παρατήρ/i;
const sameText = (a: string, b: string) => a.replace(/\r\n?/g, '\n') === b.replace(/\r\n?/g, '\n');
const GROW_NATIVE = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content');

// fallback "current" service season, used only until the app's date-based current season has loaded.
// The live value comes from useSeason().currentSeason so it stays correct as seasons roll over (it
// used to be pinned to a past season, which wrongly archived the real current boats).
const SERVICE_CURRENT_SEASON = '25-26';
// the four statuses a vessel's Washing button shows
const WASH_LABELS = ['Washing Not Scheduled', 'Washing Scheduled', 'No Wash Needed', 'Washed'];

// Smartsheet RYG-symbol option names → the app's one status vocabulary (ok green, warn amber, err red,
// info blue). 'none' is a grey / unknown value.
type Tone = 'ok' | 'warn' | 'err' | 'info' | 'none';
const TONE: Record<string, Tone> = { green: 'ok', yellow: 'warn', red: 'err', blue: 'info', gray: 'none', grey: 'none' };
const statusTone = (value: string): Tone | null => (value ? TONE[value.trim().toLowerCase()] ?? 'none' : null);
const TONE_FILL: Record<Tone, string> = {
  ok: 'var(--ok-fill)', warn: 'var(--warn-fill)', err: 'var(--err-fill)', info: 'var(--info-fill)', none: 'var(--text-3)',
};
// a row's tint from its status colour (rowTint in the page): each RYG colour, gray included.
type RowTint = 'ok' | 'warn' | 'err' | 'info' | 'gray';
const ROW_TINT: Record<string, RowTint> = { green: 'ok', yellow: 'warn', red: 'err', blue: 'info', gray: 'gray', grey: 'gray' };
// a CSS colour for a status value (filter-chip dots, the Sheet view's balls)
const statusDot = (value: string): string | null => { const t = statusTone(value); return t ? TONE_FILL[t] : null; };
/** a status ball in the status vocabulary; an empty value keeps its slot but draws nothing. `title` is a
 *  native tooltip and words for a screen reader, `sr` the words alone (a hover card explains it instead). */
const Ball = ({ tone, className = '', title, sr, ...rest }: { tone: Tone | null; className?: string; title?: string; sr?: string } & HTMLAttributes<HTMLSpanElement>) => (
  <span {...rest} className={`svc-ball ${className}`} data-tone={tone ?? 'empty'} title={title} aria-hidden={title || sr ? undefined : true}>
    {(title || sr) && <span className="sr-only">{title || sr}</span>}
  </span>
);
// the works' status roll-up as coloured counts (●2 ●0 ●1); a zero count stays, faded, so columns line up
type Roll = { done: number; total: number; ok: number; warn: number; err: number };
function RollUp({ r, noun = 'works' }: { r: Pick<Roll, 'ok' | 'warn' | 'err'>; noun?: string }) {
  const words = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}: ${r.ok} completed, ${r.warn} in progress, ${r.err} N/A (not applicable)`;
  return (
    <span className="svc-ru" role="img" aria-label={words} title={words}>
      <span className={r.ok ? '' : 'z'}><span className="svc-ball" data-tone="ok" />{r.ok}</span>
      <span className={r.warn ? '' : 'z'}><span className="svc-ball" data-tone="warn" />{r.warn}</span>
      <span className={r.err ? '' : 'z'}><span className="svc-ball" data-tone="err" />{r.err}</span>
    </span>
  );
}
// line icons the shared set doesn't carry (same 24px grid, 1.9 stroke as components/icons.tsx)
const LineIcon = ({ size = 14, children }: { size?: number; children: ReactNode }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);
const IconPlus = ({ size }: { size?: number }) => <LineIcon size={size}><path d="M12 5v14M5 12h14" /></LineIcon>;
const IconBoard = ({ size }: { size?: number }) => <LineIcon size={size}><path d="M4 4.5h4.5v15H4zM10 4.5h4.5v9H10zM16 4.5h4v12h-4z" /></LineIcon>;
const IconGantt = ({ size }: { size?: number }) => <LineIcon size={size}><path d="M4 6.5h8M8 12h10M6 17.5h7" /></LineIcon>;
const IconSheet = ({ size }: { size?: number }) => <LineIcon size={size}><rect x="3.5" y="4.5" width="17" height="15" rx="1" /><path d="M3.5 9.5h17M3.5 14.5h17M9.5 9.5v10" /></LineIcon>;

// ── build journey (matched to the overview fleet by SAP) ─────────────────────────
// the digits of a SAP number, so "J9100391" / "JS9100391" / "9100391" all match
const sapKey = (s: string): string => String(s ?? '').match(/\d{5,}/)?.[0] ?? '';
const DELIVERED: JourneyStep[] = JOURNEY_STAGES.map((s) => ({ ...s, state: 'done' }));
interface VesselJourney { steps: JourneyStep[]; label: string; tone: 'warn' | 'err' | null; fleet?: OverviewVessel }
/** the words beside the strip: "Delivered", "Sea trial on hold", "Delivers Wed 7 Oct", "Delivery overdue". */
function journeyLabel(steps: JourneyStep[], fv: OverviewVessel | undefined, day: (iso: string) => string): Pick<VesselJourney, 'label' | 'tone'> {
  if (steps.every((s) => s.state === 'done')) return { label: 'Delivered', tone: null };
  const at = steps.findIndex((s) => s.state === 'current' || s.state === 'hold' || s.state === 'late');
  if (at < 0) return { label: 'Not started', tone: null };
  const s = steps[at]!;
  if (at === steps.length - 1) {
    if (s.state === 'late') return { label: 'Delivery overdue', tone: 'err' };
    if (s.state === 'hold') return { label: 'Delivery on hold', tone: 'warn' };
    const d = fv?.deliveryDate && /^\d{4}-\d{2}-\d{2}/.test(fv.deliveryDate) ? day(fv.deliveryDate.slice(0, 10)) : '';
    return { label: d ? `Delivers ${d}` : 'Ready for delivery', tone: null };
  }
  if (s.state === 'hold') return { label: `${s.label} on hold`, tone: 'warn' };
  if (s.state === 'late') return { label: `${s.label}, over target`, tone: 'err' };
  return { label: s.label, tone: null };
}

// The columns the floor cares about most, in priority order. These are pulled to the FRONT of the
// works table and always shown as their own column — even the text ones (e.g. Comments) that would
// otherwise be tucked away in a work's extra-fields panel. Matched loosely on the title.
// NB: Season noter is deliberately NOT a priority column — it lives only in the work's extra-fields /
// tools panel (see noterCol); it still drives the season tag via seasonOf.
// the old approval dropdown (the sheet spells it "Aprooval"): Technician took its place on the works, so
// Rolodex no longer shows it; the column and its answers stay on the sheet.
const APPROVAL_TITLE = /ap+ro+v|[εέ]γκρ/i;
// the sheet's "Type of task" column is where the floor writes its comments on a work ("In progress |
// Valve tightened | Repair completed"), so Rolodex calls it Comments and edits it as free text (a box that
// grows with the comment, MULTILINE_TITLE), never as the sheet's dropdown: what is typed is saved as it is.
// The column keeps its name on the sheet
const TYPE_OF_TASK_TITLE = /type\s*of\s*(task|work)|είδος|τύπ/i;
const COMMENTS_TITLE = 'Comments';
// after the Technician column (always first: findServiceColumn('technician'))
const PRIORITY_TITLE: RegExp[] = [
  /shipyard|ναυπηγ/i,                        // @ Shipyard
  /status|κατάστασ/i,                        // Status
  /^\s*comments?\s*$|σχόλι/i,                // Comments (the sheet's Type of task)
];

// the Sheet view's sort columns: the vessel name, its group, its season tag or a sheet column (by id)
type SortKey = 'name' | 'group' | 'season' | number;

export function Service() {
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useServiceSheet();
  const write = useServiceWrite();
  const addRow = useServiceAddRow();
  const delRow = useServiceDeleteRow();
  const newVisitM = useServiceNewVisit();
  const { can } = useAuth();
  // "current" follows the app's date-based current season (26-27 now), not a hardcoded past one.
  const { currentSeason: appCurrentSeason } = useSeason();
  const currentSeason = appCurrentSeason || SERVICE_CURRENT_SEASON;
  const canWrite = can('service:write');
  const df = useDateFormatter();
  // the current season's overview fleet, for each vessel's build journey. Same query key and request as
  // useOverview(currentSeason), so it shares that cache; only fetched with overview:read.
  const canFleet = can('overview:read');
  const fleetQ = useQuery({
    queryKey: ['overview', appCurrentSeason],
    queryFn: () => api.get<OverviewResponse>(`/api/overview?season=${encodeURIComponent(appCurrentSeason)}`),
    enabled: canFleet && !!appCurrentSeason,
  });
  const hist = useCellHistoryHover(data?.sheetId);
  // a column the sheet doesn't have yet (Expected date / Flag, below) has no history to show
  const histEnter = (rowId: number, colId: number, title: string, name: string) => (colId > 0 ? hist.onEnter(rowId, colId, title, name) : undefined);
  const histPress = (rowId: number, colId: number, title: string, name: string) => (colId > 0 ? hist.bindPress(rowId, colId, title, name) : {});
  const histButton = (rowId: number, colId: number, title: string, name: string) => (colId > 0 ? hist.historyButton(rowId, colId, title, name) : null);
  const { onLeave: histLeave, popover: histPopover } = hist;
  // touch: names are text (rename lives in the fields panels / the group ✎) and field captions get a history clock
  const coarse = useIsCoarse();
  // phone: print selection is a mode (the header boxes show only while picking), seasons are plain tags and
  // the Sheet folds Group into the vessel cell
  const isPhone = useIsPhone();
  const [confirmNode, confirm] = useConfirm();
  // the hover card that names a status and says what it means (features/serviceStatus.ts)
  const tip = useHoverTip();
  const statusTip = (value: string): ReactNode => {
    const s = serviceStatusOf(value);
    if (!s) return null;
    return (
      <>
        <span className="hover-tip-t"><span className="svc-ball" data-tone={statusTone(value) ?? 'empty'} aria-hidden="true" />{s.label}</span>
        <span className="hover-tip-d">{s.description}</span>
      </>
    );
  };

  const [q, setQ] = useState('');
  const [view, setView] = useState<'board' | 'sheet' | 'gantt'>('board');
  const [group, setGroup] = useState('ALL');
  const [seasonSel, setSeasonSel] = useState('CURRENT'); // default: current season only (past seasons archived)
  const [statusSel, setStatusSel] = useState('ALL');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [showAllCols, setShowAllCols] = useState(false);
  // the Sheet view's sort: name A→Z on a first visit, then whatever this tab last picked
  const { sort, setSort, dirOf, th: sortTh } = useTableSort<SortKey>('service', { key: 'name', dir: 'asc' });
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const [detailIds, setDetailIds] = useState<Set<number>>(new Set());
  const [pinned, setPinned] = useState<Set<number>>(new Set()); // rows added this session - always shown
  const [selected, setSelected] = useState<Set<number>>(new Set()); // vessels ticked for print
  const [selecting, setSelecting] = useState(false); // phone: picking vessels to print
  const selectMode = isPhone && selecting;
  const [printErr, setPrintErr] = useState<Error | null>(null);
  const [sheetOpen, setSheetOpen] = useState<Set<number>>(new Set()); // sheet view: expanded vessels
  const [photosFor, setPhotosFor] = useState<{ id: number; name: string; source: ServicePhotoSource; files?: File[] } | null>(null); // a work's Photos, or a vessel's from the client
  const [photoDropOn, setPhotoDropOn] = useState<number | null>(null); // the camera button photos are being dragged onto
  const [entryFor, setEntryFor] = useState<{ vessel: EntryVessel; formId?: number } | null>(null); // a vessel's entry form
  const [washFor, setWashFor] = useState<number | null>(null); // a vessel's wash (by its row id)
  const [visitsFor, setVisitsFor] = useState<number | null>(null); // a boat's visits, opened from one of them (its row id)
  const inited = useRef(false);

  // share the filters/view over a co-op session so everyone sees the same thing.
  useCoopView(
    // the sort goes over the wire as it always has: a key (null = the sheet's own order) and 1 / -1
    { svView: view, svGroup: group, svSeason: seasonSel, svStatus: statusSel, svOpen: onlyOpen, svFlag: onlyFlagged, svAll: showAllCols, svQ: q, svSortKey: sort?.key ?? null, svSortDir: sort?.dir === 'desc' ? -1 : 1 },
    (v) => {
      if (v.svView === 'board' || v.svView === 'sheet' || v.svView === 'gantt') setView(v.svView);
      if (typeof v.svGroup === 'string') setGroup(v.svGroup);
      if (typeof v.svSeason === 'string') setSeasonSel(v.svSeason);
      if (typeof v.svStatus === 'string') setStatusSel(v.svStatus);
      if (typeof v.svOpen === 'boolean') setOnlyOpen(v.svOpen);
      if (typeof v.svFlag === 'boolean') setOnlyFlagged(v.svFlag);
      if (typeof v.svAll === 'boolean') setShowAllCols(v.svAll);
      if (typeof v.svQ === 'string') setQ(v.svQ);
      if ('svSortKey' in v) {
        const key = v.svSortKey as SortKey | null | undefined;
        setSort(key == null ? null : { key, dir: v.svSortDir === -1 ? 'desc' : 'asc' });
      }
    },
  );

  // the sheet's columns (never the old approval dropdown; Type of task as Comments), plus a stand-in for "Expected date", "Flag",
  // "Technician" and the vessel's estimated and actual arrival dates when the sheet has no such column yet:
  // they show and can be set like any other, and the first value set adds the real column to the
  // Smartsheet (setCell → ensureColumn). Stand-ins have negative ids.
  const columns = useMemo(() => {
    const all = data?.columns ?? [];
    if (!all.length) return all;
    const approval = all.find((c) => !c.primary && APPROVAL_TITLE.test(c.title));
    const typeOfTask = all.find((c) => !c.primary && TYPE_OF_TASK_TITLE.test(c.title));
    const cols = all
      .filter((c) => c !== approval)
      .map((c) => (c === typeOfTask ? { ...c, title: COMMENTS_TITLE, type: 'TEXT_NUMBER', options: [] } : c));
    const extra = (['expected', 'flag', 'technician', 'expectedArrival', 'arrival'] as const)
      .filter((k) => !findServiceColumn(k, cols))
      .map((k): SheetGridColumn => ({ id: SERVICE_PENDING_COLUMN_ID[k], title: SERVICE_EXTRA_COLUMNS[k].title, type: SERVICE_EXTRA_COLUMNS[k].type, options: [], primary: false, ...(SERVICE_EXTRA_COLUMNS[k].symbol ? { symbol: SERVICE_EXTRA_COLUMNS[k].symbol } : {}) }));
    return extra.length ? [...cols, ...extra] : cols;
  }, [data?.columns]);
  const primaryCol = useMemo(() => columns.find((c) => c.primary) ?? columns[0], [columns]);
  // a vessel's flag is its own icon (flagButton), never a column on the works or in the Sheet view
  const flagCol = useMemo(() => findServiceColumn('flag', columns), [columns]);
  const expectedCol = useMemo(() => findServiceColumn('expected', columns), [columns]);
  // who carried out a work: typed in, on the works only
  const technicianCol = useMemo(() => findServiceColumn('technician', columns), [columns]);
  const valueCols = useMemo(() => columns.filter((c) => c.id !== primaryCol?.id && c.id !== flagCol?.id), [columns, primaryCol, flagCol]);
  const inlineCols = useMemo(() => valueCols.filter((c) => INLINE_TYPES.has(c.type)), [valueCols]);
  const detailCols = useMemo(() => valueCols.filter((c) => !INLINE_TYPES.has(c.type)), [valueCols]);
  // Some columns belong to the BOAT, not each work. Its dates, SAP and "@ Shipyard" are shown once on the
  // vessel (a fields strip in Board, the vessel row in Sheet) and kept off every work row / task panel.
  const dateCols = useMemo(() => valueCols.filter((c) => c.type === 'DATE' || c.type === 'DATETIME'), [valueCols]);
  // the vessel's five dates, in order and by their names (features/serviceDates.ts): estimated and actual
  // arrival (the app's own columns), the work's start, and its estimated and actual completion. The
  // arrival dates are the vessel's alone: never a work's, a season's or a Gantt bar's.
  const vesselDateFields = useMemo(() => serviceVesselDateFields(dateCols), [dateCols]);
  const vesselDateLabel = useMemo(() => new Map(vesselDateFields.map((f) => [f.col.id, f.label])), [vesselDateFields]);
  const arrivalIds = useMemo(() => new Set(vesselDateFields.filter((f) => f.key === 'expectedArrival' || f.key === 'arrival').map((f) => f.col.id)), [vesselDateFields]);
  /** a column as the page names it: the vessel's dates by their names, the rest by the sheet's titles */
  const colLabel = (c: SheetGridColumn) => vesselDateLabel.get(c.id) ?? c.title;
  const sapCol = useMemo(() => valueCols.find((c) => /\bsap\b/i.test(c.title)), [valueCols]);
  const shipyardCol = useMemo(() => valueCols.find((c) => /shipyard|ναυπηγ/i.test(c.title)), [valueCols]);
  // "Difficulty" is dropped from the Board entirely (still available in the Sheet view).
  const difficultyCol = useMemo(() => valueCols.find((c) => /difficult|δυσκολ/i.test(c.title)), [valueCols]);
  // the boat-level columns, in display order (the vessel's five dates, then SAP, then @ Shipyard) + a fast
  // id set. A sheet whose dates aren't named the Service sheet's way keeps all its dates on the vessel.
  const boatCols = useMemo(() => {
    const named = vesselDateFields.map((f) => f.col);
    const out = named.some((c) => !arrivalIds.has(c.id)) ? named : [...named, ...dateCols.filter((c) => !arrivalIds.has(c.id) && c.id !== expectedCol?.id)];
    if (sapCol) out.push(sapCol);
    if (shipyardCol) out.push(shipyardCol);
    return out;
  }, [vesselDateFields, arrivalIds, dateCols, expectedCol, sapCol, shipyardCol]);
  const boatColIds = useMemo(() => new Set(boatCols.map((c) => c.id)), [boatCols]);
  // ...except a work's own Start / Finish dates: every work has its own, so they show on each work row (and
  // its fields panel and Sheet sub-row) as well as on the vessel. A sheet whose date columns aren't named
  // that way shows all of them per work, as before they moved to the boat.
  // Every work also has its own Expected date.
  const workDateCols = useMemo(() => {
    const dates = dateCols.filter((c) => !arrivalIds.has(c.id));
    const named = dates.filter((c) => c.id === expectedCol?.id || /start|begin|finish|\bend\b|complet|έναρξ|λήξ|ολοκλ|τέλος/i.test(c.title));
    return named.length > (expectedCol ? 1 : 0) ? named : dates;
  }, [dateCols, expectedCol, arrivalIds]);
  const workDateIds = useMemo(() => new Set(workDateCols.map((c) => c.id)), [workDateCols]);
  // a column that lives on the vessel only (never on a work)
  const boatOnly = (c: SheetGridColumn) => boatColIds.has(c.id) && !workDateIds.has(c.id);
  // the first colour-ball column, never the Flag column (a checkbox Smartsheet also draws as a symbol)
  const statusCol = useMemo(() => findServiceStatusColumn(columns), [columns]);
  // the priority columns (technician, shipyard, status, type of work), in that order, whatever their
  // type — surfaced first so the floor doesn't have to open a work to reach them.
  const priorityCols = useMemo(() => {
    const picked: SheetGridColumn[] = technicianCol ? [technicianCol] : [];
    const used = new Set<number>(picked.map((c) => c.id));
    for (const re of PRIORITY_TITLE) {
      const c = valueCols.find((x) => !used.has(x.id) && re.test(x.title));
      if (c) { picked.push(c); used.add(c.id); }
    }
    return picked;
  }, [valueCols, technicianCol]);
  const priorityIds = useMemo(() => new Set(priorityCols.map((c) => c.id)), [priorityCols]);
  // inline columns that are actually used somewhere on the sheet — an inline column blank on every row
  // is dead clutter, so it's hidden from the works table unless "all columns" is on (same idea the
  // Sheet view already uses).
  const usedColIds = useMemo(() => {
    const s = new Set<number>();
    for (const r of data?.rows ?? []) for (const c of inlineCols) {
      const x = r.cells[c.id];
      if (x === true || (typeof x === 'string' && x.trim() !== '')) s.add(c.id);
    }
    return s;
  }, [data, inlineCols]);
  // the per-task editor columns: priority ones first, then the remaining inline-type columns (dates,
  // other dropdowns) that are actually used. A symbol status column that ISN'T already a priority stays
  // a name-side dot.
  const taskInlineCols = useMemo(() => {
    const hidden = (c: SheetGridColumn) => (boatColIds.has(c.id) && !workDateIds.has(c.id)) || c.id === difficultyCol?.id;
    const rest = inlineCols.filter(
      (c) => !priorityIds.has(c.id) && !workDateIds.has(c.id) && !hidden(c) && !(c.id === statusCol?.id && c.symbol) && (showAllCols || usedColIds.has(c.id)),
    );
    // @ Shipyard is a priority title but it's boat-level now, so drop it (and Difficulty) from the table.
    // The work's Start / Finish follow the priority columns, always shown so a date can be set.
    return [...priorityCols.filter((c) => !hidden(c)), ...workDateCols, ...rest];
  }, [priorityCols, priorityIds, inlineCols, statusCol, usedColIds, showAllCols, boatColIds, difficultyCol, workDateCols, workDateIds]);
  const statusIsColumn = useMemo(() => taskInlineCols.some((c) => c.id === statusCol?.id), [taskInlineCols, statusCol]);
  // A checkbox column only counts as a completion signal when its TITLE says so — never just because
  // it's the first checkbox (an "Invoiced" / "Urgent" box must not be read as "work done").
  const doneCol = useMemo(
    () => columns.find((c) => c.type === 'CHECKBOX' && /done|complete|✓|ok|ολοκλ|finish|τελ/i.test(c.title)),
    [columns],
  );
  // the columns a vessel's season is read from: the "Season noter" staff fill on the works (the authoritative
  // service season), an explicit Season column, and a service / delivery date that's neither a work's
  // Expected date nor a vessel's arrival date (features/serviceSeason.ts, shared with the Washing Plan)
  const seasonCols = useMemo(() => serviceSeasonColumns(columns), [columns]);
  const { seasonCol, noterCol } = seasonCols;

  const childrenOf = useMemo(() => {
    const m = new Map<number | null, SheetGridRow[]>();
    for (const r of data?.rows ?? []) {
      const p = r.parentId ?? null;
      (m.get(p) ?? m.set(p, []).get(p)!).push(r);
    }
    return m;
  }, [data]);
  const kids = (id: number) => childrenOf.get(id) ?? [];
  const rawName = (r: SheetGridRow) => (primaryCol ? cellStr(r.cells[primaryCol.id]) : '');
  const label = (r: SheetGridRow) => rawName(r) || '(unnamed)';
  const isNoise = (r: SheetGridRow) => !rawName(r).trim() && kids(r.id).length === 0 && !pinned.has(r.id);

  const groups = useMemo(
    () => (childrenOf.get(null) ?? []).filter((r) => kids(r.id).length > 0 || pinned.has(r.id)),
    [childrenOf, pinned], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const groupOf = useMemo(() => {
    const m = new Map<number, SheetGridRow>();
    const byId = new Map((data?.rows ?? []).map((r) => [r.id, r]));
    for (const g of groups) for (const v of kids(g.id)) m.set(v.id, g);
    return { byVessel: m, byId };
  }, [groups, data]); // eslint-disable-line react-hooks/exhaustive-deps

  // one season per vessel with its source (features/serviceSeason.ts): the "Season noter" on the vessel, or
  // the most common one among its works; else an explicit season column, the build season, then a date.
  const seasonOf = useMemo(() => {
    const m = new Map<number, RowSeason>();
    for (const g of groups) {
      for (const v of kids(g.id)) {
        if (isNoise(v)) continue;
        const s = serviceVesselSeason(v, kids, seasonCols);
        if (s) m.set(v.id, s);
      }
    }
    return m;
  }, [data, groups, seasonCols]); // eslint-disable-line react-hooks/exhaustive-deps
  const seasonId = (id: number): string | undefined => seasonOf.get(id)?.id;

  useEffect(() => {
    if (data && !inited.current) { inited.current = true; setOpenIds(new Set(groups.map((r) => r.id))); }
  }, [data, groups]);

  // ── task completion roll-up ────────────────────────────────────────────────
  const taskDone = (r: SheetGridRow): boolean => {
    if (doneCol && r.cells[doneCol.id] === true) return true;
    if (statusCol) {
      const s = cellStr(r.cells[statusCol.id]).trim();
      // a text status that reads "complete/done/…", or on an RYG symbol column a ball that closes the work:
      // green (Completed) or red (N/A, it doesn't apply), as the sheet's own Completion column counts them
      if (s && (DONE_RE.test(s) || (statusCol.symbol && serviceStatusCloses(s)))) return true;
    }
    return false;
  };
  // a row takes its Status colour: green, yellow, red, blue or gray tints the whole row to match (Board,
  // Sheet and the printout). Any other status text leaves the row as it is.
  const rowTint = (r: SheetGridRow): RowTint | null =>
    statusCol ? ROW_TINT[cellStr(r.cells[statusCol.id]).trim().toLowerCase()] ?? null : null;
  const rowTintCls = (r: SheetGridRow): string => { const t = rowTint(r); return t ? ` svc-row svc-row--${t}` : ''; };
  // leaf works under a vessel (a work with no sub-works is a leaf) → {done,total} and their status counts.
  const rollup = (vesselId: number): Roll => {
    const r: Roll = { done: 0, total: 0, ok: 0, warn: 0, err: 0 };
    const walk = (pid: number) => {
      for (const t of kids(pid).filter((k) => !isNoise(k))) {
        const sub = kids(t.id).filter((k) => !isNoise(k));
        if (sub.length) walk(t.id);
        else {
          r.total++;
          if (taskDone(t)) r.done++;
          const tone = statusCol ? statusTone(cellStr(t.cells[statusCol.id])) : null;
          if (tone === 'ok' || tone === 'warn' || tone === 'err') r[tone]++;
        }
      }
    };
    walk(vesselId);
    return r;
  };
  const hasCompletionSignal = !!doneCol || !!statusCol;
  // the status roll-up only means something on an RYG-style status column
  const hasRollUp = !!statusCol && (!!statusCol.symbol || statusCol.options.some((o) => statusTone(o) !== 'none'));

  // ── build journey: the vessel's SAP matched against the current overview fleet ──
  const fleetBySap = useMemo(() => {
    if (!fleetQ.data) return null;
    const m = new Map<string, OverviewVessel>();
    for (const fv of fleetQ.data.vessels) { const k = sapKey(fv.sap); if (k) m.set(k, fv); }
    return m;
  }, [fleetQ.data]);
  const journeyNow = useMemo(() => new Date(), [fleetQ.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const fmtDay = (iso: string) => df.date(iso, { weekday: 'short', year: 'auto' });
  // ── service journey: where the boat is in its service, from @ Shipyard, its works and its dates ──
  const svcDates = useMemo(() => {
    const expectRe = /expect|εκτιμ|αναμεν|αναμέν/i;
    const dates = dateCols.filter((c) => !arrivalIds.has(c.id));
    const of = (key: string) => vesselDateFields.find((f) => f.key === key)?.col;
    return {
      start: dates.find((c) => /start|begin|έναρξ|εναρξ/i.test(c.title)),
      expected: dates.find((c) => expectRe.test(c.title)),
      delivery: dates.find((c) => /deliver|παράδοσ|παραδοσ/i.test(c.title) && !expectRe.test(c.title)),
      expectedArrival: of('expectedArrival'),
      arrival: of('arrival'),
    };
  }, [dateCols, arrivalIds, vesselDateFields]);
  const todayIso = localTodayIso();
  const serviceJourneyFor = (v: SheetGridRow, roll: Roll): ServiceJourney | null => {
    if (!shipyardCol) return null;
    const d = (c: SheetGridColumn | undefined) => (c ? cellStr(v.cells[c.id]) : '');
    return serviceJourney(
      yardStateOf(v.cells[shipyardCol.id]),
      roll,
      { start: d(svcDates.start), expected: d(svcDates.expected), delivery: d(svcDates.delivery), expectedArrival: d(svcDates.expectedArrival), arrival: d(svcDates.arrival) },
      todayIso,
      (iso) => df.date(iso, { year: 'auto' }), // no weekday: the label sits in a narrow column
    );
  };
  const journeyFor = (v: SheetGridRow): VesselJourney | null => {
    if (!fleetBySap) return null; // no fleet to compare with: draw nothing rather than guess
    const key = sapCol ? sapKey(cellStr(v.cells[sapCol.id])) : '';
    const fleet = key ? fleetBySap.get(key) : undefined;
    const steps = fleet ? journeyOf(fleet, journeyNow) : DELIVERED;
    return { steps, fleet, ...journeyLabel(steps, fleet, fmtDay) };
  };

  // ── search: keep matching rows + their ancestors ───────────────────────────
  // the search box updates `q` instantly but the heavy filtering keys off a deferred copy, so typing
  // in a large sheet stays responsive instead of reconciling the whole grid on every keystroke.
  const deferredQ = useDeferredValue(q);
  const keep = useMemo(() => {
    // accents, the Greek tonos and a final ς don't matter; a tick never matches the word "true"
    const query = fold(deferredQ.trim());
    if (!query) return null;
    const byId = new Map((data?.rows ?? []).map((r) => [r.id, r]));
    const set = new Set<number>();
    for (const r of data?.rows ?? []) {
      if (!Object.values(r.cells).some((c) => hasText(query, c))) continue;
      let cur: SheetGridRow | undefined = r;
      while (cur) { set.add(cur.id); cur = cur.parentId != null ? byId.get(cur.parentId) : undefined; }
    }
    return set;
  }, [data, deferredQ]);

  const isOpen = (id: number) => (keep ? true : openIds.has(id));
  const toggle = (id: number) => setOpenIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleDetails = (id: number) => setDetailIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // a stand-in column (no such column on the sheet yet) is added to the Smartsheet first, then written.
  // One request per kind at a time, so two quick edits don't both try to add it.
  const ensureColumn = useServiceEnsureColumn();
  const ensuring = useRef<Partial<Record<ServiceExtraKind, Promise<number>>>>({});
  const realColumnId = (col: SheetGridColumn): Promise<number> => {
    if (col.id > 0) return Promise.resolve(col.id);
    const kind: ServiceExtraKind = serviceExtraKindOf(col.id) ?? 'expected';
    return (ensuring.current[kind] ??= ensureColumn.mutateAsync(kind)
      .then((r) => r.column.id)
      .finally(() => { delete ensuring.current[kind]; }));
  };
  const setCell = (rowId: number, col: SheetGridColumn, value: ServiceCellValue) => {
    if (col.id > 0) { write.mutate({ rowId, columnId: col.id, value }); return; }
    realColumnId(col).then((columnId) => write.mutate({ rowId, columnId, value }), () => { /* shown by ensureColumn.error */ });
  };
  const flagged = (v: SheetGridRow): boolean => !!flagCol && v.cells[flagCol.id] === true;
  // the vessel's flag: a red flag when set, a faint outline to set it (editors); viewers see set flags only
  const flagButton = (v: SheetGridRow, cls = ''): ReactNode => {
    if (!flagCol) return null;
    const on = flagged(v);
    if (!canWrite) return on ? <span className={`svc-flag on ${cls}`} title="Flagged" role="img" aria-label="Flagged"><IconFlag size={15} filled /></span> : null;
    return (
      <button
        type="button"
        className={`svc-flag${on ? ' on' : ''} ${cls}`}
        aria-pressed={on}
        aria-label={`${on ? 'Remove the flag from' : 'Flag'} ${label(v)}`}
        title={on ? 'Flagged - click to remove the flag' : 'Flag this vessel'}
        onClick={(e) => { e.stopPropagation(); setCell(v.id, flagCol, !on); }}
      ><IconFlag size={15} filled={on} /></button>
    );
  };
  // a vessel's entry form: the customer's Vessel Entry & Work Control Form, kept as the Documents page's
  // boat yard record with the vessel by its row id. The button sits by the vessel's name (faint until a
  // form is saved); the open card lists the saved ones. A new form is filled in from the vessel's row (its
  // name, model, SAP and dates). Without edit access the button shows only once there is a form to read
  // and print.
  const boatDocs = useBoatDocs();
  const formsOf = useMemo(() => formsByVessel(boatDocs.data?.items ?? []), [boatDocs.data]);
  const vesselForms = (v: SheetGridRow): BoatDocRecord[] => formsOf.get(String(v.id)) ?? [];
  const entryVessel = (v: SheetGridRow): EntryVessel => ({
    rowId: v.id, name: rawName(v), sap: sapCol ? cellStr(v.cells[sapCol.id]).trim() : '', fleetModel: journeyFor(v)?.fleet?.model,
    dates: serviceVesselDates(columns, v.cells),
  });
  const formWhen = (f: BoatDocRecord): string =>
    f.entryDate && f.exitDate ? df.range(f.entryDate, f.exitDate) : f.entryDate ? df.date(f.entryDate) : f.exitDate ? `left ${df.date(f.exitDate)}` : 'no dates yet';
  const entryButton = (v: SheetGridRow, cls = ''): ReactNode => {
    // the form it opens: the visit in progress, else the newest
    const current = formToOpen(vesselForms(v));
    if (!current && !canWrite) return null;
    const state = !current ? 'none' : isClosed(current) ? 'closed' : 'open';
    return (
      <button
        type="button"
        className={`svc-ef ${cls}`}
        data-state={state}
        aria-label={`Entry form for ${label(v)}${current ? `, ${formWhen(current)}` : ', none yet'}`}
        title={current ? `Entry form, ${formWhen(current)} - open it to ${canWrite ? 'edit or ' : ''}print` : 'Fill in the vessel entry & work control form'}
        onClick={(e) => { e.stopPropagation(); setEntryFor({ vessel: entryVessel(v) }); }}
      ><IconEntryForm size={15} /><span className="svc-ef-t">Entry form</span></button>
    );
  };
  // a vessel's wash: the Washing Plan's service wash for it (one per vessel, by its row id). Its status reads
  // from the plan: no washing required, washed, planned for a day (late once the day has gone by), to wash
  // (the service is finished and the boat is still here) or nothing yet. A vessel whose service isn't
  // finished can still have its wash planned ahead, or marked as needing none.
  const washes = useWashes();
  const setWash = useSetWash();
  const washRecs = useMemo(() => new Map((washes.data?.washes ?? []).map((r) => [r.id, r])), [washes.data]);
  const washVessels = useMemo(() => (data ? serviceWashVessels(data, todayIso, currentSeason) : new Map()), [data, todayIso, currentSeason]);
  const washItems = useMemo(
    () => new Map((data ? serviceWashes(data, washRecs, todayIso, currentSeason) : []).map((i) => [i.boatKey, i])),
    [data, washRecs, todayIso, currentSeason],
  );
  const washEntry = (rowId: number): WashEntry => {
    const key = String(rowId);
    const id = washId('service', key, 'service');
    const sv = washVessels.get(key);
    const record = washRecs.get(id);
    return {
      id, source: 'service', boatKey: key, kind: 'service',
      name: sv?.name ?? record?.boatName ?? '', model: sv?.model ?? record?.boatModel ?? '', sap: sv?.sap ?? '',
      record, item: washItems.get(key), boat: sv ? { model: sv.model, sap: sv.sap, pct: null, delivery: sv.delivery } : undefined,
    };
  };
  const washContext = (rowId: number): string | undefined => {
    const sv = washVessels.get(String(rowId));
    if (!sv) return undefined;
    if (!sv.current) return `A past season's visit (${sv.season}): the Washing Plan doesn't list it`;
    if (!sv.here) return 'Not at the shipyard: plan it ahead, or mark it as needing no wash';
    if (!sv.finished) return 'Service in progress: the works aren\'t all finished yet';
    return undefined;
  };
  // the four statuses the Washing button shows, the same words on every vessel (the day is in the wash
  // window, never on the button): Washing Not Scheduled (amber once the service is finished and the wash is
  // due), Washing Scheduled (red once its day has passed unwashed), No Wash Needed, Washed
  type WashState = 'unscheduled' | 'scheduled' | 'skip' | 'done';
  const washStatus = (rowId: number): { state: WashState; tone?: 'due' | 'late'; text: string; title: string } => {
    const e = washEntry(rowId);
    const r = e.record;
    const day = (d: string) => df.date(d, { year: 'auto' });
    if (r?.doneOn) return { state: 'done', text: 'Washed', title: 'Washed (the day it was washed is in the wash window)' };
    if (r?.notNeededOn) return { state: 'skip', text: 'No Wash Needed', title: 'No washing required' };
    if (r?.plannedOn) {
      const late = r.plannedOn < todayIso;
      return {
        state: 'scheduled', tone: late ? 'late' : undefined, text: 'Washing Scheduled',
        title: late ? `Washing scheduled for ${day(r.plannedOn)}, not washed yet` : `Washing scheduled for ${day(r.plannedOn)}`,
      };
    }
    if (e.item) return { state: 'unscheduled', tone: 'due', text: 'Washing Not Scheduled', title: 'Washing not scheduled: the service is finished, the wash is due' };
    return { state: 'unscheduled', text: 'Washing Not Scheduled', title: 'Washing not scheduled: no washing date yet' };
  };
  const washButton = (v: SheetGridRow, cls = ''): ReactNode => {
    const s = washStatus(v.id);
    const Icon = s.state === 'skip' ? IconDropOff : s.state === 'done' ? IconDropDone : IconDrop;
    return (
      <button
        type="button"
        className={`svc-ef svc-wash ${cls}`}
        data-state={s.state}
        data-tone={s.tone}
        aria-label={`Washing for ${label(v)}: ${s.title}`}
        title={`${s.title} - open it to ${canWrite ? 'schedule, tick or mark it' : 'see it'}`}
        onClick={(e) => { e.stopPropagation(); setWashFor(v.id); }}
      >
        <Icon size={15} />
        {/* every status's words share the label's one cell, the others unseen, so the button is as wide as the
            longest whatever it says: the Entry form beside it stays put from vessel to vessel */}
        <span className="svc-ef-t svc-wash-t">
          {WASH_LABELS.map((t) => <span key={t} className={t === s.text ? undefined : 'svc-wash-sz'} aria-hidden={t === s.text ? undefined : true}>{t}</span>)}
        </span>
      </button>
    );
  };
  // a saved form prints straight from the card: the entry & work control form, or (a record saved before
  // it, with nothing of it) the plain entry form as before, in the forms' language last picked on this device
  const printForm = (f: BoatDocRecord) => {
    const ok = isControlForm(f)
      ? printVesselEntry(f, { date: (iso) => df.date(iso) })
      : printBoatDoc('entry', f, df.date, readDocLang());
    setPrintErr(ok ? null : new Error(PRINT_BLOCKED));
  };
  const add = (parentId?: number) =>
    // Smartsheet rejects an add with an empty cells array, so seed the primary column (blank) like the
    // GRP page does — otherwise "+ Add group/vessel/work" silently creates nothing.
    addRow.mutate({ cells: primaryCol ? [{ columnId: primaryCol.id, value: '' }] : [], parentId }, {
      onSuccess: (res) => {
        if (res?.id) setPinned((p) => new Set(p).add(res.id));
        if (parentId) setOpenIds((p) => new Set(p).add(parentId));
      },
    });
  const remove = async (r: SheetGridRow) => {
    const n = kids(r.id).length;
    if (await confirm({ title: 'Delete row', message: `Delete “${label(r)}”${n ? ` and its ${n} sub-row(s)` : ''} from Smartsheet?`, confirmLabel: 'Delete', danger: true })) delRow.mutate({ rowId: r.id });
  };
  const togglePrint = (id: number) =>
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── filter options (season / status), derived from the actual vessels ──────
  const allVessels = useMemo(() => groups.flatMap((g) => kids(g.id).filter((v) => !isNoise(v))), [groups]); // eslint-disable-line react-hooks/exhaustive-deps
  // a boat comes in several times a season, each visit a vessel row of its own: every vessel's place among its
  // boat's visits (features/serviceVisits.ts), told by the SAP, numbered within the season
  const visitsOf = useMemo(
    () => serviceVisits(allVessels.map((v) => ({
      id: v.id, name: rawName(v).trim(), sap: sapCol ? cellStr(v.cells[sapCol.id]) : '', season: seasonId(v.id) ?? '', dates: serviceVisitDates(v, seasonCols),
    }))),
    [allVessels, seasonOf, sapCol, seasonCols], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const seasonOptions = useMemo(() => {
    const set = new Set<string>();
    for (const v of allVessels) { const s = seasonId(v.id); if (s) set.add(s); }
    return [...set].sort(compareSeasonIds);
  }, [allVessels, seasonOf]); // eslint-disable-line react-hooks/exhaustive-deps
  const anyUntagged = useMemo(() => allVessels.some((v) => !seasonId(v.id)), [allVessels, seasonOf]); // eslint-disable-line react-hooks/exhaustive-deps
  const statusOptions = useMemo(() => {
    if (!statusCol) return [];
    const set = new Set<string>();
    for (const v of allVessels) { const s = cellStr(v.cells[statusCol.id]).trim(); if (s) set.add(s); }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allVessels, statusCol]);

  // a vessel passes the season / status / open filters (search is handled by `keep`).
  // `ignoreStatus` → the same test without the status filter (the status chips' counts)
  const vesselPasses = (v: SheetGridRow, ignoreStatus = false): boolean => {
    const rs = seasonOf.get(v.id);
    const s = rs?.id;
    if (seasonSel === 'CURRENT') {
      // archive only a boat whose season comes from a real service signal (the noter, an explicit Season
      // column or a service/delivery date). A boat merely BUILT in an older season is still a
      // current-service vessel — never hide it just for its build year.
      if (!isCurrentServiceSeason(rs, currentSeason)) return false;
    } else if (seasonSel === '__none') { if (s) return false; }
    else if (seasonSel !== 'ALL') { if (s !== seasonSel) return false; }
    if (!ignoreStatus && statusSel !== 'ALL' && statusCol && cellStr(v.cells[statusCol.id]).trim() !== statusSel) return false;
    if (onlyOpen) { const { done, total } = rollup(v.id); if (total === 0 || done >= total) return false; }
    if (onlyFlagged && !flagged(v)) return false;
    return true;
  };
  const matchesSearch = (v: SheetGridRow): boolean =>
    !keep || keep.has(v.id) || kids(v.id).some((t) => keep.has(t.id));

  // vessels visible under the current filters, across all groups.
  const filteredVessels = useMemo(
    // a row added this session is always shown (even when a filter is active) so a just-added blank
    // vessel can be named — otherwise "+ Add vessel" under a filter would create invisible orphan rows.
    () => allVessels.filter((v) => pinned.has(v.id) || (vesselPasses(v) && matchesSearch(v) && (group === 'ALL' || groupOf.byVessel.get(v.id)?.id === Number(group)))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allVessels, seasonSel, statusSel, onlyOpen, onlyFlagged, keep, group, seasonOf, data, pinned, currentSeason, flagCol],
  );
  // how many vessels each status chip would show, under every other filter ('' = the All chip)
  const statusCounts = useMemo(() => {
    const m = new Map<string, number>();
    if (!statusCol) return m;
    for (const v of allVessels) {
      if (!pinned.has(v.id) && !(vesselPasses(v, true) && matchesSearch(v) && (group === 'ALL' || groupOf.byVessel.get(v.id)?.id === Number(group)))) continue;
      m.set('', (m.get('') ?? 0) + 1);
      const s = cellStr(v.cells[statusCol.id]).trim();
      if (s) m.set(s, (m.get(s) ?? 0) + 1);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allVessels, seasonSel, onlyOpen, onlyFlagged, keep, group, seasonOf, data, pinned, currentSeason, statusCol, flagCol]);
  // O(1) membership sets so the board/group render doesn't do nested scans over filteredVessels.
  const filteredIds = useMemo(() => new Set(filteredVessels.map((v) => v.id)), [filteredVessels]);
  const groupsWithFiltered = useMemo(() => {
    const s = new Set<number>();
    for (const v of filteredVessels) { const g = groupOf.byVessel.get(v.id); if (g) s.add(g.id); }
    return s;
  }, [filteredVessels, groupOf]);

  // ── KPIs ───────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    let open = 0, done = 0, total = 0;
    for (const v of filteredVessels) { const r = rollup(v.id); total += r.total; done += r.done; if (r.total > r.done) open++; }
    return { vessels: filteredVessels.length, withOpen: open, doneTasks: done, totalTasks: total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredVessels, data]);

  const lastSync = dataUpdatedAt ? df.time(dataUpdatedAt) : null;
  const seasonChip = (id: number): ReactNode => {
    const s = seasonId(id);
    if (!s) return null;
    // phone: a plain tag. A stray tap on a 15px chip used to flip the whole page's Season filter; the
    // filter itself stays in the filter bar.
    if (isPhone) return <span className={`svc-season ${s === currentSeason ? 'cur' : ''}`} title={`Season ${s}${s === currentSeason ? ' (current)' : ''}`}>{s}</span>;
    return (
      <button
        type="button"
        className={`svc-season ${s === currentSeason ? 'cur' : ''}`}
        title={`Season ${s}${s === currentSeason ? ' (current)' : ''} - filter by it`}
        aria-label={`Filter by season ${s}`}
        aria-pressed={seasonSel === s}
        onClick={(e) => { e.stopPropagation(); setSeasonSel((p) => (p === s ? 'ALL' : s)); }}
      >
        {s}
      </button>
    );
  };
  // "Visit 2 of 3" when the boat is in more than once in the vessel's season, else how many visits the sheet has
  // of it (none for a boat's only visit); a click lists them
  const visitText = (id: number): string => { const x = visitsOf.get(id)?.visit; return x && x.of > 1 ? `Visit ${x.n} of ${x.of}` : ''; };
  const visitChip = (v: SheetGridRow): ReactNode => {
    const info = visitsOf.get(v.id);
    if (!info || info.all.length < 2) return null;
    const { n, of, season } = info.visit;
    const text = visitText(v.id) || `${info.all.length} visits`;
    const title = of > 1 ? `Visit ${n} of ${of} of this boat in ${season}` : `This boat has ${info.all.length} visits on the sheet`;
    return (
      <button type="button" className={`svc-visit${of > 1 ? ' many' : ''}`} title={`${title}: see them all`} aria-label={`${title}: see them all`} onClick={(e) => { e.stopPropagation(); newVisitM.reset(); setVisitsFor(v.id); }}>
        <IconRefresh size={11} />{text}
      </button>
    );
  };
  // another visit opens as a link to it would: shown whatever the filters, its group and card open
  const showVisit = (id: number) => {
    setVisitsFor(null);
    setParams((p) => { const n = new URLSearchParams(p); n.set(VESSEL_PARAM, String(id)); return n; }, { replace: true });
  };
  // the boat back for another visit: a new vessel at the top of its group, with its name and SAP, Expected,
  // this season (routes/service.ts serviceNewVisitRoute), then shown like a link to it
  const newVisit = async (v: SheetGridRow) => {
    const season = seasonIdFor(new Date());
    const ok = await confirm({
      title: 'New visit',
      message: `Add another visit of “${label(v)}”? A new vessel goes at the top of its group with the same name and SAP, @ Shipyard Expected and Season Noter ${season}. Its works, dates, forms, photos and wash start empty.`,
      confirmLabel: 'Add visit',
    });
    if (!ok) return;
    newVisitM.mutate({ rowId: v.id }, {
      onSuccess: (res) => {
        setVisitsFor(null);
        if (!res?.id) return;
        setPinned((p) => new Set(p).add(res.id));
        setParams((p) => { const n = new URLSearchParams(p); n.set(VESSEL_PARAM, String(res.id)); return n; }, { replace: true });
      },
    });
  };
  const meta = (r: SheetGridRow): ReactNode => {
    const bits: ReactNode[] = [];
    if (r.attachmentCount) bits.push(<span key="a" className="svc-meta" title={`${r.attachmentCount} file(s) in Smartsheet`}><IconPaperclip size={12} /> {r.attachmentCount}</span>);
    if (r.commentCount) bits.push(<span key="c" className="svc-meta" title={`${r.commentCount} comment(s) in Smartsheet`}><IconChat size={12} /> {r.commentCount}</span>);
    return bits.length ? <>{bits}</> : null;
  };

  // photos dragged over the page are meant for a camera button: one dropped beside it does nothing (the
  // browser would otherwise open the file in place of the app). The Photos dialog takes drops itself, when
  // photos can be added there (read-only, it takes none, so this guard stays on).
  useEffect(() => {
    if (photosFor && canWrite) return;
    const guard = (e: globalThis.DragEvent) => {
      if (e.defaultPrevented || !dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      if (e.type === 'drop') setPhotoDropOn(null);
      else if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', guard);
    window.addEventListener('drop', guard);
    return () => {
      window.removeEventListener('dragover', guard);
      window.removeEventListener('drop', guard);
    };
  }, [photosFor, canWrite]);
  // photos dragged from the computer onto a button that opens photos: the dialog opens and they go straight up
  const photoDrop = (row: SheetGridRow, source: ServicePhotoSource) => {
    const over = (e: DragEvent<HTMLButtonElement>) => {
      if (!canWrite || !dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (photoDropOn !== row.id) setPhotoDropOn(row.id);
    };
    return {
      onDragEnter: over,
      onDragOver: over,
      onDragLeave: () => setPhotoDropOn((id) => (id === row.id ? null : id)),
      onDrop: (e: DragEvent<HTMLButtonElement>) => {
        if (!canWrite || !dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        setPhotoDropOn(null);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) setPhotosFor({ id: row.id, name: label(row), source, files });
      },
    };
  };
  const photoButton = (row: SheetGridRow, cls = '') => (
    <button
      type="button"
      className={`svc2-iact${cls ? ` ${cls}` : ''}${photoDropOn === row.id ? ' is-drop' : ''}`}
      title={canWrite ? 'Photos (or drop photos here)' : 'Photos'}
      aria-label={`Photos for ${label(row)}`}
      onClick={() => setPhotosFor({ id: row.id, name: label(row), source: 'yard' })}
      {...photoDrop(row, 'yard')}
    ><IconCamera size={15} /></button>
  );
  // a vessel's photos from the client (by email or WhatsApp, say): of the boat, not of one work, so they sit
  // with the vessel, beside its Entry form; kept in Smartsheet in the vessel row's comments
  const clientPhotosButton = (v: SheetGridRow, cls = ''): ReactNode => (
    <button
      type="button"
      className={`svc-ef svc-cph ${cls}${photoDropOn === v.id ? ' is-drop' : ''}`}
      aria-label={`Photos from the client for ${label(v)}`}
      title={canWrite ? 'Photos from the client (or drop them here)' : 'Photos from the client'}
      onClick={(e) => { e.stopPropagation(); setPhotosFor({ id: v.id, name: label(v), source: 'client' }); }}
      {...photoDrop(v, 'client')}
    ><IconCamera size={15} /><span className="svc-ef-t">Client photos</span></button>
  );

  // ── cell editors (used by both views) ──────────────────────────────────────
  const editor = (col: SheetGridColumn, row: SheetGridRow, full = false) => {
    const val = row.cells[col.id];
    const put = (v: ServiceCellValue) => setCell(row.id, col, v);
    if (col.type === 'CHECKBOX')
      return <input type="checkbox" checked={val === true} disabled={!canWrite} onChange={(e) => put(e.target.checked)} title={col.title} />;
    if (col.type === 'DATE' || col.type === 'DATETIME') {
      // editors pick the date; everyone else reads it as text in their own date format
      if (!canWrite) return <span title={col.title}>{df.cell(col.type, val) || '-'}</span>;
      // saves once per edit (a whole date, or on leaving the field), never a half-typed year per keystroke
      return <DateField value={typeof val === 'string' ? val.slice(0, 10) : ''} onCommit={put} title={col.title} />;
    }
    if (col.type === 'MULTI_PICKLIST')
      return <TagCell value={cellStr(val)} options={col.options} disabled={!canWrite} onChange={(vals) => put(vals)} />;
    if (col.type === 'PICKLIST' && col.options.length)
      return (
        <select className="field" value={typeof val === 'string' ? val : ''} disabled={!canWrite} onChange={(e) => put(e.target.value || null)} title={col.id === statusCol?.id ? undefined : col.title} aria-label={col.title} style={full ? { width: '100%' } : undefined}>
          <option value="" />
          {/* the Status colours by their names (N/A – Not Applicable, In Progress, …); the sheet keeps the colour */}
          {col.options.map((o) => <option key={o} value={o}>{col.id === statusCol?.id ? serviceStatusLabel(o) : o}</option>)}
        </select>
      );
    const text = cellStr(val);
    if (/[\r\n]/.test(text) || MULTILINE_TITLE.test(col.title))
      return <GrowText key={text} value={text} disabled={!canWrite} title={col.title} onCommit={put} />;
    // a single-line value: the guard compares against the text as an <input> holds it (no line breaks)
    return <input key={text} className="field" defaultValue={text} disabled={!canWrite} onBlur={(e) => { if (e.target.value !== text.replace(/[\r\n]/g, '')) put(e.target.value); }} style={{ width: '100%' }} title={col.title} />;
  };

  // a work's (or vessel's) whole name when its box cuts it short, while the pointer is on it
  const fullNameTip = (text: string) => tip.bind(<span className="hover-tip-full">{text}</span>);
  // a live rename input with a mouse; wrapping text on touch (see NameField), with an optional ✎.
  const nameInput = (r: SheetGridRow, cls: string, textCls: string, placeholder: string, name: string, rename = false) => (
    <NameField
      value={rawName(r)}
      canWrite={canWrite}
      className={cls}
      textClassName={textCls}
      placeholder={placeholder}
      ariaLabel={name}
      rename={rename}
      fullTip={fullNameTip}
      onCommit={(v) => { if (primaryCol) setCell(r.id, primaryCol, v); }}
    />
  );

  // the extra-fields panel never shows boat-level columns (SAP / @ Shipyard / the vessel's dates — those are
  // edited once on the vessel; a work's own Start / Finish do show) nor Difficulty (dropped from the Board).
  // a tag editor is never wrapped in <label>: the label would forward caption/tag clicks to the first
  // tag's × button and silently remove it.
  const fieldWrap = (c: SheetGridColumn) => (c.type === 'MULTI_PICKLIST' ? 'div' : 'label');
  // on touch the name is plain text in the header, so it is renamed here: Name leads the panel. The
  // history clock sits after the editor in the DOM so a <label> still points at its own control.
  const detailsPanel = (row: SheetGridRow, vessel = false) => (
    <div className="svc2-details">
      {coarse && canWrite && primaryCol && (
        <label className="svc2-field svc2-field-name">
          <span className="field-label">Name</span>
          {editor(primaryCol, row, true)}
        </label>
      )}
      {/* a vessel's dates are in its fields strip already; a work's Start / Finish are its own */}
      {valueCols.filter((c) => !(vessel ? boatColIds.has(c.id) || c.id === technicianCol?.id : boatOnly(c)) && c.id !== difficultyCol?.id).map((c) => {
        const Wrap = fieldWrap(c);
        return (
          <Wrap key={c.id} className={`svc2-field ${c.type === 'CHECKBOX' ? 'chk' : ''}`}>
            <span className="field-label">{c.title}{c.symbol ? ' •' : ''}</span>
            {editor(c, row, true)}
            {histButton(row.id, c.id, c.title, rawName(row))}
          </Wrap>
        );
      })}
    </div>
  );

  // ── print (one page per ticked vessel) ─────────────────────────────────────
  const printSelected = () => {
    if (!selected.size || !primaryCol) return;
    // keep the printout lean: only the columns the floor acts on (technician, status, type of work), not
    // every column on the sheet. Boat-level columns (dates, SAP, @ Shipyard) go in the vessel header line.
    const cols = (priorityCols.length ? priorityCols : statusCol ? [statusCol] : []).filter((c) => !boatColIds.has(c.id));
    const fmtCell = (c: SheetGridColumn, r: SheetGridRow) => (c.type === 'CHECKBOX' ? (r.cells[c.id] === true ? '✓' : '') : c.id === statusCol?.id ? serviceStatusLabel(cellStr(r.cells[c.id])) : df.cell(c.type, r.cells[c.id]));
    const collect = (vid: number) => {
      const acc: Array<{ row: SheetGridRow; depth: number }> = [];
      const walk = (pid: number, depth: number) => {
        for (const t of kids(pid).filter((k) => !isNoise(k))) { acc.push({ row: t, depth }); walk(t.id, depth + 1); }
      };
      walk(vid, 0);
      return acc;
    };
    const body = allVessels
      .filter((r) => selected.has(r.id))
      .map((v, i) => {
        const flat = collect(v.id);
        const groupName = rawName(groupOf.byVessel.get(v.id) ?? ({} as SheetGridRow));
        const season = seasonId(v.id);
        const boatInfo = boatCols.filter((c) => c.id > 0).map((c) => { const val = df.cell(c.type, v.cells[c.id]); return val ? `${colLabel(c)}: ${val}` : ''; }).filter(Boolean).join(' · ');
        const head = `<tr><th style="width:55%">Work</th>${cols.map((c) => `<th>${esc(c.title)}</th>`).join('')}</tr>`;
        const rows = flat.length
          ? flat.map(({ row, depth }) => {
              // a work prints in its status colour (print-color-adjust in renderPrint forces it).
              const tint = rowTint(row);
              const tr = tint ? `<tr class="${tint}">` : '<tr>';
              return `${tr}<td>${'&nbsp;'.repeat(depth * 3)}${esc(rawName(row) || '-')}</td>${cols.map((c) => `<td>${esc(fmtCell(c, row))}</td>`).join('')}</tr>`;
            }).join('')
          : `<tr><td colspan="${cols.length + 1}"><i>No works.</i></td></tr>`;
        const sub = [flagged(v) ? '⚑ Flagged' : '', groupName, season ? `Season ${season}` : '', boatInfo].filter(Boolean).join(' · ');
        return `<section style="page-break-inside:avoid;${i > 0 ? 'page-break-before:always;' : ''}margin-bottom:14px"><h1>${esc(rawName(v) || '(unnamed)')}</h1><div class="sub">${esc(sub)}</div><table><thead>${head}</thead><tbody>${rows}</tbody></table></section>`;
      })
      .join('');
    setPrintErr(printSchedule('Service', body) ? null : new Error('The print window was blocked. Allow pop-ups for this site and try again.'));
  };

  // ── CSV export of the visible vessels (all columns) ────────────────────────
  const exportCsv = () => {
    // the sheet's own columns only (a stand-in for a column the sheet doesn't have yet would be empty)
    const csvCols = valueCols.filter((c) => c.id > 0);
    const withFlag = !!flagCol && flagCol.id > 0;
    const header = ['Group', 'Vessel', 'Season', ...(withFlag ? ['Flagged'] : []), ...csvCols.map((c) => c.title)];
    const lines = filteredVessels.map((v) => [
      rawName(groupOf.byVessel.get(v.id) ?? ({} as SheetGridRow)),
      rawName(v),
      seasonId(v.id) ?? '',
      ...(withFlag ? [flagged(v) ? 'Yes' : ''] : []),
      ...csvCols.map((c) => (c.type === 'CHECKBOX' ? (v.cells[c.id] === true ? 'Yes' : '') : cellStr(v.cells[c.id]))),
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => (/[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join(','))
      .join('\r\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service-${localTodayIso()}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ── board view renderers ───────────────────────────────────────────────────
  // one work = one <tr>; its sub-works and (when expanded) its extra-fields panel are flattened into
  // sibling <tr>s so the whole thing lives under a single header row that labels every column.
  const renderTaskRows = (row: SheetGridRow, depth: number): ReactNode[] => {
    const children = kids(row.id).filter((k) => !isNoise(k));
    const hasKids = children.length > 0;
    const showing = detailIds.has(row.id);
    const stVal = statusCol ? cellStr(row.cells[statusCol.id]) : '';
    const st = statusTone(stVal);
    const out: ReactNode[] = [];
    out.push(
      <tr key={row.id} className={`svc2-trow${rowTintCls(row)}`} style={{ '--depth': depth } as CSSProperties}>
        <td className="svc2-tname-td">
          <div className="svc2-tname">
            {hasKids ? (
              <button
                type="button"
                className={`svc2-tchev${isOpen(row.id) ? ' is-open' : ''}`}
                aria-label={`${isOpen(row.id) ? 'Collapse' : 'Expand'} ${label(row)}`}
                aria-expanded={isOpen(row.id)}
                onClick={() => toggle(row.id)}
              ><IconChevronRight size={14} /></button>
            ) : <span className="svc2-tchev empty" aria-hidden="true" />}
            {!statusIsColumn && st && <Ball tone={st} sr={`Status: ${serviceStatusText(stVal)}`} {...tip.bind(statusTip(stVal))} />}
            {nameInput(row, 'svc2-tin', 'svc2-tname-txt', 'Describe the work…', 'Work name')}
            {meta(row)}
          </div>
        </td>
        {taskInlineCols.map((c) => {
          const v = cellStr(row.cells[c.id]);
          const isDone = c.id === doneCol?.id;
          return (
            <td
              key={c.id}
              className={`svc2-tcell${priorityIds.has(c.id) ? ' pri' : ''}${isDone ? ' svc2-tdone' : ''}${c.id === statusCol?.id ? ' svc2-tstatus' : ''}`}
              data-label={c.title}
              onPointerEnter={histEnter(row.id, c.id, c.title, rawName(row))}
              onPointerLeave={histLeave}
              {...histPress(row.id, c.id, c.title, rawName(row))}
            >
              {/* status: the ball, then its value */}
              {c.symbol ? <span className="svc-cellv" {...tip.bind(c.id === statusCol?.id ? statusTip(v) : null)}><Ball tone={statusTone(v)} />{editor(c, row)}</span>
                // phone: the Done tick sits at the row's top right in a 44px box (the comp's tick column)
                : isDone && isPhone ? <label className="svc-tdone-hit">{editor(c, row)}</label>
                // phone: any other tick box becomes a 44px labelled toggle under its caption, the whole box the target
                : c.type === 'CHECKBOX' && isPhone ? <label className="svc2-tchk">{editor(c, row)}<span>{row.cells[c.id] === true ? 'Yes' : 'No'}</span></label>
                : editor(c, row)}
              {histButton(row.id, c.id, c.title, rawName(row))}
            </td>
          );
        })}
        <td className="svc2-tact-td">
          {photoButton(row)}
          {(detailCols.length > 0 || (coarse && canWrite)) && <button type="button" className={`svc2-iact ${showing ? 'on' : ''}`} title="More fields" aria-label={`More fields for ${label(row)}`} aria-expanded={showing} onClick={() => toggleDetails(row.id)}><IconMore size={16} /></button>}
          {canWrite && <button type="button" className="svc2-iact" title="Add sub-work" aria-label={`Add sub-work under ${label(row)}`} disabled={addRow.isPending} onClick={() => add(row.id)}><IconPlus size={15} /></button>}
          {canWrite && <button type="button" className="svc2-iact del" title="Delete" aria-label={`Delete ${label(row)}`} disabled={delRow.isPending} onClick={() => void remove(row)}><IconTrash size={14} /></button>}
        </td>
      </tr>,
    );
    if (showing) out.push(
      <tr key={`${row.id}-d`} className="svc2-trow-detail">
        <td colSpan={2 + taskInlineCols.length}>{detailsPanel(row)}</td>
      </tr>,
    );
    if (hasKids && isOpen(row.id)) for (const k of children) out.push(...renderTaskRows(k, depth + 1));
    return out;
  };

  const renderVessel = (v: SheetGridRow): ReactNode => {
    const tasks = kids(v.id).filter((t) => !isNoise(t) && (!keep || keep.has(t.id) || keep.has(v.id)));
    const open = isOpen(v.id);
    const showing = detailIds.has(v.id);
    const st = statusCol ? cellStr(v.cells[statusCol.id]) : '';
    const roll = rollup(v.id);
    const journey = journeyFor(v);
    // the strip follows the boat's service (@ Shipyard, its works, its dates); a sheet without an
    // @ Shipyard column keeps the build journey
    const service = serviceJourneyFor(v, roll);
    // the line under the name: SAP · model (from the matched fleet boat) · the next detail value. The
    // season is shown once, as the tag, so any season-like value (build year, season column, noter) stays
    // out of it, whichever column it sits in.
    const sap = sapCol ? cellStr(v.cells[sapCol.id]).trim() : '';
    const vm = [
      sap,
      journey?.fleet?.model ?? '',
      ...detailCols
        .filter((c) => c.id !== sapCol?.id && c.id !== statusCol?.id && c.id !== seasonCol?.id && c.id !== noterCol?.id)
        .map((c) => cellStr(v.cells[c.id]))
        .filter((s) => s && s !== 'false' && !normalizeSeasonId(s))
        .slice(0, sap ? 1 : 2),
    ].filter(Boolean).join(' · ');
    const works = hasCompletionSignal && roll.total > 0 ? roll.total : tasks.length;
    const forms = vesselForms(v);
    const visitTag = visitChip(v);
    return (
      <div key={v.id} data-vessel-id={v.id} className={`svc2-card ${open ? 'open' : ''}${selectMode && selected.has(v.id) ? ' picked' : ''}`}>
        {/* while picking vessels to print (phone), a tap on the header ticks the vessel instead of opening it */}
        <div className="svc2-vhd" onClick={() => (selectMode ? togglePrint(v.id) : toggle(v.id))}>
          <input type="checkbox" className="svc-vsel" checked={selected.has(v.id)} onClick={(e) => e.stopPropagation()} onChange={() => togglePrint(v.id)} aria-label={`Select ${label(v)} for print`} title="Select for print" />
          <button
            type="button"
            className={`svc2-vchev${open ? ' is-open' : ''}`}
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${label(v)}`}
            onClick={(e) => { e.stopPropagation(); toggle(v.id); }}
          ><IconChevronRight size={16} /></button>
          <Ball tone={statusTone(st)} className="svc-vball" sr={st ? `Status: ${serviceStatusText(st)}` : undefined} {...tip.bind(statusTip(st))} />
          {flagButton(v)}
          <div className="svc-vt">
            {nameInput(v, 'svc2-vname-in', 'svc2-vname', 'Vessel name…', 'Vessel name')}
            {(vm || visitTag || meta(v)) && <span className="svc-vm"><span className="svc-vm-t">{vm}</span>{visitTag}{meta(v)}</span>}
          </div>
          <span className="svc-vbtns">{entryButton(v)}{clientPhotosButton(v)}{washButton(v)}</span>
          {seasonChip(v.id)}
          {service ? (
            <span className="svc-lc" title={service.summary}>
              <JourneyStrip steps={service.steps} summary={service.summary} stepWord={serviceStepWord} />
              <span className="svc-lc-t" data-tone={service.tone ?? undefined}>{service.label}</span>
            </span>
          ) : !shipyardCol && journey && (
            <span className="svc-lc" title={journeySummary(journey.steps)}>
              <JourneyStrip steps={journey.steps} />
              <span className="svc-lc-t" data-tone={journey.tone ?? undefined}>{journey.label}</span>
            </span>
          )}
          <span className="svc-wk">{works} work{works === 1 ? '' : 's'}</span>
          {hasRollUp && roll.total > 0 && <RollUp r={roll} />}
          {hasCompletionSignal && roll.total > 0 && (
            <span className={`svc-dn${roll.done >= roll.total ? ' all' : ''}`} title="Works done (Completed or N/A) / all works"><b>{roll.done}</b>/{roll.total} done</span>
          )}
        </div>
        {open && (
          <div className="svc2-vbd">
            {(boatCols.length > 0 || canWrite || forms.length > 0) && (
              <div className="svc2-vfields">
                {boatCols.map((c) => {
                  const Wrap = fieldWrap(c);
                  return (
                    <Wrap key={c.id} className="svc2-vfield">
                      <span className="svc2-vfield-lbl">{colLabel(c)}</span>
                      {editor(c, v)}
                    </Wrap>
                  );
                })}
                {forms.length > 0 && (
                  <span className="svc-efs">
                    {forms.map((f) => (
                      <span key={f.id} className="svc-ef-item" data-closed={isClosed(f) ? '' : undefined}>
                        <IconEntryForm size={14} />
                        <span className="svc-ef-when"><span className="svc-ef-k">{isControlForm(f) ? CONTROL_FORM_NAME : 'Entry form'}<span className="svc-ef-dot"> · </span></span><span className="svc-ef-d">{formWhen(f)}</span></span>
                        <button type="button" className="svc2-iact" title={`Print this ${isControlForm(f) ? CONTROL_FORM_NAME.toLowerCase() : 'entry form'}`} onClick={() => printForm(f)}><IconPrinter size={14} /> Print</button>
                        <button type="button" className="svc2-iact" title={canWrite ? 'Change this entry form' : 'Read this entry form'} onClick={() => setEntryFor({ vessel: entryVessel(v), formId: f.id })}>{canWrite ? 'Edit' : 'View'}</button>
                      </span>
                    ))}
                  </span>
                )}
                {canWrite && (
                  <span className="svc2-vacts">
                    {(valueCols.length > 0 || coarse) && <button type="button" className={`svc2-iact ${showing ? 'on' : ''}`} title="Edit vessel fields" aria-expanded={showing} onClick={() => toggleDetails(v.id)}><IconTool size={14} /> Fields</button>}
                    {rawName(v).trim() && <button type="button" className="svc2-iact" title="This boat is back: add its next visit" disabled={newVisitM.isPending} onClick={() => void newVisit(v)}><IconRefresh size={14} /> New visit</button>}
                    <button type="button" className="svc2-iact del" title="Delete vessel" disabled={delRow.isPending} onClick={() => void remove(v)}><IconTrash size={14} /> Delete</button>
                  </span>
                )}
              </div>
            )}
            {showing && detailsPanel(v, true)}
            {tasks.length ? (
              <div className="tbl-wrap svc2-tasks">
                <table className="svc2-ttable">
                  <thead>
                    <tr>
                      <th className="svc2-tname-th">Work</th>
                      {taskInlineCols.map((c) => <th key={c.id} className={`${priorityIds.has(c.id) ? 'svc2-th-pri' : ''}${c.id === doneCol?.id ? ' svc2-th-done' : ''}${c.id === technicianCol?.id ? ' svc2-th-tech' : ''}${c.id === statusCol?.id ? ' svc2-th-status' : ''}${c.title === COMMENTS_TITLE ? ' svc2-th-comments' : ''}`} title={c.title}>{c.title}</th>)}
                      <th className="svc2-tact-th" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>{tasks.flatMap((t) => renderTaskRows(t, 0))}</tbody>
                </table>
              </div>
            ) : <div className="muted svc-none">No works yet.</div>}
            {canWrite && (
              <div className="svc-vfoot">
                <button type="button" className="svc2-add" disabled={addRow.isPending} onClick={() => add(v.id)}><IconPlus size={15} /> Add work</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const boardGroups = groups.filter(
    // keep a group that has a matching vessel, OR one just added this session (so its "+ Add vessel"
    // button is reachable — otherwise "+ Group" would be a dead-end).
    (g) => (group === 'ALL' || g.id === Number(group)) && (groupsWithFiltered.has(g.id) || pinned.has(g.id)),
  );

  // ── sheet view (flat, sortable table of vessels) ───────────────────────────
  const sheetCols = useMemo(() => {
    // Season noter lives only in the per-work fields/tools panel, so it's not a Sheet-view column.
    const base = valueCols.filter((c) => c.id !== noterCol?.id);
    if (showAllCols) return base;
    // hide columns that are empty for every visible vessel, so the table stays scannable. A work's Start /
    // Finish count when any of the vessel's works has one (they're often blank on the vessel row itself).
    const filled = (r: SheetGridRow, id: number) => { const x = r.cells[id]; return x === true || (typeof x === 'string' && x.trim() !== ''); };
    const inWorks = (vid: number, id: number): boolean => kids(vid).some((k) => filled(k, id) || inWorks(k.id, id));
    // (and the Technician, which only works have)
    return base.filter((c) => filteredVessels.some((v) => filled(v, c.id) || ((workDateIds.has(c.id) || c.id === technicianCol?.id) && inWorks(v.id, c.id))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueCols, filteredVessels, showAllCols, noterCol, workDateIds, childrenOf, technicianCol]);

  // the shared table sort (lib/tableSort): blanks sink either way, numbers and ticks as numbers, text in
  // Greek + English dictionary order. No sort = the sheet's own order.
  const sortedVessels = useMemo(
    () => sortRows(filteredVessels, sort, (v, key) => {
      if (key === 'name') return rawName(v);
      if (key === 'group') { const g = groupOf.byVessel.get(v.id); return g ? rawName(g) : ''; }
      if (key === 'season') return seasonId(v.id);
      return v.cells[key];
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredVessels, sort, seasonOf, groupOf, data],
  );

  const toggleSheet = (id: number) => setSheetOpen((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // the vessel cell itself opens / closes its works (the row already reads as clickable); a press on the
  // rename input, the chevron or any other control inside keeps its own job.
  const sheetNameClick = (id: number) => (e: MouseEvent<HTMLElement>) => {
    if (!(e.target as HTMLElement).closest('input, select, textarea, button, a')) toggleSheet(id);
  };
  // a vessel's works (and their sub-works), pre-order with depth — for the expandable Sheet rows.
  const descendantWorks = (vid: number): Array<{ row: SheetGridRow; depth: number }> => {
    const acc: Array<{ row: SheetGridRow; depth: number }> = [];
    const walk = (pid: number, depth: number) => {
      for (const t of kids(pid).filter((k) => !isNoise(k))) { acc.push({ row: t, depth }); walk(t.id, depth + 1); }
    };
    walk(vid, 0);
    return acc;
  };

  // ── Gantt: each vessel's works on a timeline, by their own Start / Finish ──
  const writeCells = useServiceWriteCells();
  // the work's start and finish columns: named so ("Start", "Finish", "Έναρξη", "Λήξη", "Due"), else the
  // first two work date columns; a sheet with one date column plots single days
  const ganttCols = useMemo(() => {
    const start = dateCols.find((c) => /start|begin|έναρξ/i.test(c.title));
    const finish = dateCols.find((c) => c !== start && /finish|\bend\b|complet|due|deadline|λήξ|ολοκλ|τέλος|προθεσμ/i.test(c.title));
    if (start && finish) return { start, finish };
    // the bars run Start → Finish; a work's Expected date isn't one of them
    const wd = workDateCols.filter((c) => c.id !== expectedCol?.id);
    const [a, b] = start ? [start, wd.find((c) => c !== start)] : [wd[0], wd[1]];
    return a ? { start: a, finish: b ?? a } : null;
  }, [dateCols, workDateCols, expectedCol]);
  const typeCol = useMemo(() => valueCols.find((c) => /type\s*of\s*(task|work)|είδος|τύπ/i.test(c.title)), [valueCols]);
  const assigneeCol = useMemo(() => valueCols.find((c) => /assign|ανατέθ|υπεύθυν/i.test(c.title)) ?? valueCols.find((c) => c.type === 'CONTACT_LIST'), [valueCols]);
  // every type in a fixed order (the column's options, then any others seen), so a type keeps its colour
  const typeOrder = useMemo(() => {
    if (!typeCol) return [];
    const seen = new Set(typeCol.options);
    const extra = new Set<string>();
    for (const r of data?.rows ?? []) { const t = cellStr(r.cells[typeCol.id]).trim(); if (t && !seen.has(t)) extra.add(t); }
    return [...typeCol.options, ...[...extra].sort((a, b) => a.localeCompare(b))];
  }, [typeCol, data]);
  const ganttVessels = (): GanttVessel[] => {
    if (!ganttCols) return [];
    return sortedVessels.map((v) => ({
      row: v,
      name: rawName(v),
      sub: [
        sapCol ? cellStr(v.cells[sapCol.id]).trim() : '',
        rawName(groupOf.byVessel.get(v.id) ?? ({} as SheetGridRow)),
        visitText(v.id),
      ].filter(Boolean).join(' · '),
      works: descendantWorks(v.id).map(({ row, depth }) => {
        const st = statusCol ? cellStr(row.cells[statusCol.id]).trim() : '';
        return {
          row,
          name: rawName(row),
          depth,
          start: cellStr(row.cells[ganttCols.start.id]).slice(0, 10),
          finish: cellStr(row.cells[ganttCols.finish.id]).slice(0, 10),
          done: taskDone(row),
          tone: statusTone(st),
          status: serviceStatusLabel(st),
          type: typeCol ? cellStr(row.cells[typeCol.id]).trim() : '',
          assignee: assigneeCol ? cellStr(row.cells[assigneeCol.id]).trim() : '',
        };
      }),
    }));
  };
  // Start and Finish in one write (a single date column gets the one value)
  const setWorkDates = (row: SheetGridRow, start: string | null, finish: string | null) => {
    if (!ganttCols) return;
    const cells = [{ columnId: ganttCols.start.id, value: start }];
    if (ganttCols.finish.id !== ganttCols.start.id) cells.push({ columnId: ganttCols.finish.id, value: finish });
    writeCells.mutate({ rowId: row.id, cells });
  };
  // "Show in Board": the vessel's group and card open, scrolled into view
  const showInBoard = (v: SheetGridRow) => {
    const g = groupOf.byVessel.get(v.id);
    setOpenIds((p) => { const n = new Set(p); if (g) n.add(g.id); n.add(v.id); return n; });
    setView('board');
    revealVesselCard(v.id);
  };
  // a link to one vessel's service record (/service?vessel=<rowId>: the Calendar's entries, and any page that
  // links a vessel): once the sheet is in, the vessel shows whatever the filters were, its group and card open
  // in the Board with every work and sub-work showing, scrolled into view, and the parameter leaves the
  // address bar. A row that isn't a vessel on the sheet (any more) gets a polite note instead, but only once a
  // grid fetched after the link still lacks it: the cached one may predate a vessel added a moment ago.
  const [params, setParams] = useSearchParams();
  const linkedVessel = params.get(VESSEL_PARAM);
  // the note under the filters: still looking (a fresh grid is on its way), or the vessel isn't on the sheet
  const [linkNote, setLinkNote] = useState<'looking' | 'gone' | null>(null);
  const linkSeen = useRef<{ value: string; at: number; refetched: boolean; done: boolean } | null>(null);
  useEffect(() => {
    // no link (or it was left before it resolved): nothing to look for
    if (linkedVessel == null) { linkSeen.current = null; setLinkNote((n) => (n === 'looking' ? null : n)); return; }
    if (linkSeen.current?.value !== linkedVessel) linkSeen.current = { value: linkedVessel, at: Date.now(), refetched: false, done: false };
    const seen = linkSeen.current;
    // acted on already: the parameter is on its way out of the address bar
    if (!data || seen.done) return;
    const id = parseVesselParam(linkedVessel);
    const row = id == null ? undefined : vesselOfRow(data.rows, id);
    const v = row && allVessels.find((x) => x.id === row.id);
    // a value that isn't a row id at all needs no fresh grid to know
    const step = id == null ? 'gone' : vesselLinkStep({ found: !!v, linkedAt: seen.at, updatedAt: dataUpdatedAt, fetching: isFetching, refetched: seen.refetched });
    if (step === 'refetch') { seen.refetched = true; void refetch(); }
    if (step === 'refetch' || step === 'wait') { setLinkNote('looking'); return; }
    seen.done = true;
    setLinkNote(v ? null : 'gone');
    if (v) {
      if (!filteredIds.has(v.id)) {
        clearFilters();
        // a vessel of an archived season shows in that season (Current would still hide it)
        const s = seasonOf.get(v.id);
        if (s && s.source !== 'build' && s.id !== currentSeason) setSeasonSel(s.id);
      }
      const nested = descendantWorks(v.id).filter(({ row: w }) => kids(w.id).some((k) => !isNoise(k))).map(({ row: w }) => w.id);
      if (nested.length) setOpenIds((p) => new Set([...p, ...nested]));
      showInBoard(v);
    }
    setParams((p) => withoutVesselParam(p), { replace: true });
  }, [linkedVessel, data, dataUpdatedAt, isFetching]); // eslint-disable-line react-hooks/exhaustive-deps

  const sheetCell = (c: SheetGridColumn, v: SheetGridRow): ReactNode => {
    const tint = v.fmt?.[c.id];
    const dot = c.symbol ? statusDot(cellStr(v.cells[c.id])) : null;
    // Read-only viewers see plain text — no need to mount a live control in every cell (a big sheet in
    // Sheet view is vessels × columns, so this keeps the DOM light for people who can't edit anyway).
    const isStatus = c.id === statusCol?.id;
    const body = !canWrite ? (isStatus ? serviceStatusLabel(cellStr(v.cells[c.id])) : df.cell(c.type, v.cells[c.id])) : editor(c, v);
    return (
      <td
        key={c.id}
        style={tint ? { background: tint, color: '#1a1a1a' } : undefined}
        onPointerEnter={histEnter(v.id, c.id, c.title, rawName(v))}
        onPointerLeave={histLeave}
        {...histPress(v.id, c.id, c.title, rawName(v))}
      >
        {dot ? <span className="svc-sheetcell" {...tip.bind(isStatus ? statusTip(cellStr(v.cells[c.id])) : null)}><span className="grp-dot" style={{ background: dot }} />{body}</span> : body}
      </td>
    );
  };

  const archivedSeasons = seasonOptions.filter((s) => s !== currentSeason);
  const activeFilters = (q.trim() ? 1 : 0) + (group !== 'ALL' ? 1 : 0) + (seasonSel !== 'CURRENT' ? 1 : 0) + (statusSel !== 'ALL' ? 1 : 0) + (onlyOpen ? 1 : 0) + (onlyFlagged ? 1 : 0);
  const clearFilters = () => { setQ(''); setGroup('ALL'); setSeasonSel('CURRENT'); setStatusSel('ALL'); setOnlyOpen(false); setOnlyFlagged(false); };
  const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
  // the page's facts, as one line under the title: "11 vessels in 4 groups, 9 with open work · 40 works, 28 open · synced 17:55"
  const pageMeta = data && allVessels.length > 0 ? (
    <>
      {plural(kpis.vessels, 'vessel')} in {plural(groupsWithFiltered.size, 'group')}
      {kpis.withOpen > 0 ? `, ${kpis.withOpen} with open work` : ''}
      {hasCompletionSignal && kpis.totalTasks > 0 ? ` · ${plural(kpis.totalTasks, 'work')}, ${kpis.totalTasks - kpis.doneTasks} open` : ''}
      {lastSync ? ` · synced ${lastSync}` : ''}
    </>
  ) : undefined;

  return (
    <>
      <PageHeader title="Service" meta={pageMeta}>
        <Segmented
          value={view}
          onChange={setView}
          ariaLabel="View"
          options={[
            { value: 'board', label: <><IconBoard size={15} /> Board</> },
            { value: 'sheet', label: <><IconSheet size={15} /> Sheet</> },
            { value: 'gantt', label: <><IconGantt size={15} /> Gantt</> },
          ]}
        />
        {!isPhone && <button className="btn btn-sm" disabled={!selected.size} onClick={printSelected} title="Print the selected vessels (one per page)"><IconPrinter size={14} /> Print{selected.size ? ` (${selected.size})` : ''}</button>}
        {/* phone: these fold into ⋯, and Print becomes a picking mode with its own bottom bar */}
        <OverflowActions
          items={[
            isPhone && !selecting && { label: 'Select to print', icon: <IconPrinter size={14} />, onClick: () => { setView('board'); setSelecting(true); }, title: 'Pick vessels to print, one per page' },
            { label: 'CSV', icon: <IconDownload size={14} />, onClick: exportCsv, disabled: !filteredVessels.length, title: 'Download the visible vessels as CSV' },
            { label: isFetching ? 'Syncing…' : 'Sync', icon: isFetching ? <LogoSpin size={15} decorative /> : undefined, onClick: () => void refetch(), disabled: isFetching },
          ]}
        />
        {canWrite && <button className="btn btn-sm btn-primary" disabled={addRow.isPending} onClick={() => add()} title="Add a group" aria-label="Add group"><IconPlus size={15} /> Group</button>}
      </PageHeader>

      {data && groups.length > 0 && (
        <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder="Search vessels / works" width={210} />} activeCount={activeFilters} onClear={clearFilters}>
          <FilterSelect
            label="Group"
            value={group}
            onChange={setGroup}
            options={[{ value: 'ALL', label: 'All groups' }, ...groups.map((g) => ({ value: String(g.id), label: rawName(g) || 'New group' }))]}
          />
          {(seasonOptions.length > 0 || anyUntagged) && (
            <FilterSelect
              label="Season"
              value={seasonSel}
              onChange={setSeasonSel}
              options={[
                { value: 'CURRENT', label: `Current${currentSeason ? ` (${currentSeason})` : ''}` },
                { value: 'ALL', label: 'All seasons' },
                ...(anyUntagged ? [{ value: '__none', label: 'Untagged' }] : []),
                ...archivedSeasons.map((s) => ({ value: s, label: s })),
              ]}
            />
          )}
          {statusOptions.length > 0 && (
            <ChipGroup label="Status">
              <Chip active={statusSel === 'ALL'} onClick={() => setStatusSel('ALL')}>All <b className="svc-chipn">{statusCounts.get('') ?? 0}</b></Chip>
              {statusOptions.map((s) => (
                <span key={s} className="hover-tip-wrap" {...tip.bind(statusTip(s))}>
                  <Chip active={statusSel === s} dot={statusDot(s) || undefined} onClick={() => setStatusSel(statusSel === s ? 'ALL' : s)}>{serviceStatusShort(s)} <b className="svc-chipn">{statusCounts.get(s) ?? 0}</b></Chip>
                </span>
              ))}
            </ChipGroup>
          )}
          {hasCompletionSignal && (
            <FilterToggle checked={onlyOpen} onChange={setOnlyOpen} title="Only vessels that still have unfinished works">has open work</FilterToggle>
          )}
          {flagCol && <FilterToggle checked={onlyFlagged} onChange={setOnlyFlagged} title="Only flagged vessels">flagged</FilterToggle>}
          <FilterToggle checked={showAllCols} onChange={setShowAllCols}>all columns</FilterToggle>
        </FilterBar>
      )}

      {isLoading && <LoadingState label="Loading service sheet…" />}
      {/* a failed background poll keeps the board usable: inline error, cleared by the next poll / Sync */}
      {error && !data && <ErrorState error={error} onRetry={() => void refetch()} />}
      {error && data && <InlineError error={error} />}
      {(write.isError || addRow.isError || delRow.isError || ensureColumn.isError || newVisitM.isError) && (
        <InlineError error={write.error ?? addRow.error ?? delRow.error ?? ensureColumn.error ?? newVisitM.error} onDismiss={() => { write.reset(); addRow.reset(); delRow.reset(); ensureColumn.reset(); newVisitM.reset(); }} />
      )}
      {printErr && <InlineError error={printErr} onDismiss={() => setPrintErr(null)} />}
      {setWash.isError && <InlineError error={setWash.error} onDismiss={() => setWash.reset()} />}
      {linkNote && (
        <div className={`svc-linknote${linkNote === 'looking' ? ' svc-linknote--looking' : ''}`} role="status">
          {linkNote === 'looking'
            ? <span>Looking for the linked vessel on the Service sheet…</span>
            : <span>The linked vessel isn't on the Service sheet any more. It may have been removed in Smartsheet.</span>}
          {linkNote === 'gone' && <button type="button" className="svc-linknote-x" aria-label="Dismiss" title="Dismiss" onClick={() => setLinkNote(null)}><IconClose size={14} /></button>}
        </div>
      )}

      {/* ── GANTT ── */}
      {data && primaryCol && view === 'gantt' && (
        writeCells.isError ? <InlineError error={writeCells.error} onDismiss={() => writeCells.reset()} /> : null
      )}
      {data && primaryCol && view === 'gantt' && (
        !ganttCols
          ? <EmptyState title="No dates to plot" hint="The Service sheet has no date columns for its works. Add Start and Finish date columns in Smartsheet to plan works here." />
          : sortedVessels.length === 0
            ? <EmptyState title="No vessels match" hint={activeFilters > 0 ? 'Try clearing the filters above.' : 'No vessels in this view.'} />
            : (
              <ServiceGantt
                vessels={ganttVessels()}
                typeOrder={typeOrder}
                hasStatus={!!statusCol}
                hasType={!!typeCol}
                singleDate={ganttCols.start.id === ganttCols.finish.id}
                canWrite={canWrite}
                onSetDates={setWorkDates}
                onShowInBoard={showInBoard}
              />
            )
      )}

      {/* ── BOARD ── */}
      {data && primaryCol && view === 'board' && boardGroups.length > 0 && (
        <div className="svc-board">
          {boardGroups.map((g) => {
            const vessels = kids(g.id).filter((v) => !isNoise(v) && (filteredIds.has(v.id) || pinned.has(v.id)));
            const gOpen = isOpen(g.id);
            // the group's works by status, summed over the vessels it shows
            const gRoll = vessels.reduce((a, v) => { const r = rollup(v.id); a.ok += r.ok; a.warn += r.warn; a.err += r.err; a.total += r.total; return a; }, { ok: 0, warn: 0, err: 0, total: 0 });
            const addVessel = canWrite && (
              <button
                type="button"
                className={isPhone ? 'svc2-add' : 'btn btn-ghost btn-sm svc-gadd'}
                disabled={addRow.isPending}
                onClick={(e) => { e.stopPropagation(); add(g.id); }}
                title={`Add a vessel to ${rawName(g) || 'this group'}`}
                aria-label={`Add vessel to ${rawName(g) || 'this group'}`}
              ><IconPlus size={15} /> {isPhone ? 'Add vessel' : 'Vessel'}</button>
            );
            return (
              <div key={g.id} className={`svc2-group svc-grp${selectMode ? ' selecting' : ''}`}>
                <div className="svc2-ghd" onClick={() => toggle(g.id)}>
                  <button
                    type="button"
                    className={`svc2-chev${gOpen ? ' is-open' : ''}`}
                    aria-expanded={gOpen}
                    aria-label={`${gOpen ? 'Collapse' : 'Expand'} ${rawName(g) || 'group'}`}
                    onClick={(e) => { e.stopPropagation(); toggle(g.id); }}
                  ><IconChevronRight size={16} /></button>
                  <h2 className="svc-gh">{nameInput(g, 'svc2-gname-in', 'svc2-gname', 'Group name…', 'Group name', true)}</h2>
                  <span className="svc2-gcount">{plural(vessels.length, 'vessel')}</span>
                  {hasRollUp && gRoll.total > 0 && <RollUp r={gRoll} />}
                  {!isPhone && addVessel}
                </div>
                {gOpen && (
                  <div className="svc2-gbd">
                    {vessels.map((v) => renderVessel(v))}
                    {vessels.length === 0 && <div className="muted svc-none">No vessels match.</div>}
                    {isPhone && addVessel}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Board with groups present but every vessel filtered out — the map above renders nothing, so say so. */}
      {data && primaryCol && view === 'board' && groups.length > 0 && boardGroups.length === 0 && (
        <EmptyState title="No vessels match" hint={activeFilters > 0 ? 'Try adjusting or clearing the filters above.' : 'No vessels in this view.'} />
      )}

      {/* ── SHEET ── */}
      {data && primaryCol && view === 'sheet' && (
        // grid-fill: this grid is the page's one vertical scroller, so the header row and the pinned vessel
        // column stick; Group rides under the vessel name on phones, so the first swipe shows Status.
        <div className="tbl-wrap is-grid grid-fill">
          <table className="tbl grp-tbl">
            <thead>
              <tr>
                <th {...sortTh('name', 'grp-name-col')}>Vessel<SortMark dir={dirOf('name')} /></th>
                {!isPhone && <th {...sortTh('group')}>Group<SortMark dir={dirOf('group')} /></th>}
                {(seasonOptions.length > 0 || anyUntagged) && <th {...sortTh('season')}>Season<SortMark dir={dirOf('season')} /></th>}
                {sheetCols.map((c) => (
                  <th key={c.id} {...sortTh(c.id)} title={c.title}>{colLabel(c)}{c.symbol ? ' •' : ''}<SortMark dir={dirOf(c.id)} /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedVessels.map((v) => {
                const works = descendantWorks(v.id);
                const vOpen = sheetOpen.has(v.id);
                return (
                  <Fragment key={v.id}>
                    {/* data-row-id: a co-op pointer, field or scroll position lands on this row for everyone */}
                    <tr data-row-id={v.id} className={`${works.length ? 'grp-row-parent' : ''}${rowTintCls(v)}`}>
                      <td className="grp-name-col" onClick={works.length ? sheetNameClick(v.id) : undefined}>
                        <span className="svc-sheet-nm">
                          {works.length ? (
                            <button
                              type="button"
                              className={`svc2-tchev${vOpen ? ' is-open' : ''}`}
                              aria-expanded={vOpen}
                              aria-label={`${vOpen ? 'Collapse' : 'Expand'} ${label(v)}`}
                              onClick={() => toggleSheet(v.id)}
                            ><IconChevronRight size={14} /></button>
                          ) : <span className="svc2-tchev empty" aria-hidden="true" />}
                          {flagButton(v)}
                          {nameInput(v, 'svc-sheetname', 'svc-sheetname-txt', 'Vessel…', 'Vessel name')}
                          {isPhone && <span className="svc-sheet-grp">{rawName(groupOf.byVessel.get(v.id) ?? ({} as SheetGridRow))}</span>}
                          {visitChip(v)}
                          {meta(v)}
                          {works.length > 0 && <span className="svc-sheet-wc muted">{works.length}</span>}
                          {entryButton(v, 'svc-ef--icon')}
                          {clientPhotosButton(v, 'svc-ef--icon')}
                          {washButton(v, 'svc-ef--icon')}
                        </span>
                      </td>
                      {!isPhone && <td className="muted">{rawName(groupOf.byVessel.get(v.id) ?? ({} as SheetGridRow))}</td>}
                      {(seasonOptions.length > 0 || anyUntagged) && <td>{seasonChip(v.id) || <span className="muted">-</span>}</td>}
                      {sheetCols.map((c) => sheetCell(c, v))}
                    </tr>
                    {vOpen && works.map(({ row, depth }) => (
                      <tr key={`${v.id}-${row.id}`} data-row-id={row.id} className={`svc-subrow${rowTintCls(row)}`}>
                        <td className="grp-name-col" style={{ paddingLeft: 10 + (depth + 1) * 16 }}>
                          <span className="svc-sheet-nm">
                            {nameInput(row, 'svc-sheetname', 'svc-sheetname-txt', 'Work…', 'Work name')}{meta(row)}
                            {photoButton(row, 'svc-ph-btn')}
                          </span>
                        </td>
                        {!isPhone && <td />}
                        {(seasonOptions.length > 0 || anyUntagged) && <td />}
                        {/* boat-level columns (SAP, @ Shipyard, the vessel's own dates) stay on the vessel row, so a
                            work row leaves them blank; its Start / Finish are its own */}
                        {sheetCols.map((c) => (boatOnly(c) ? <td key={c.id} /> : sheetCell(c, row)))}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
              {sortedVessels.length === 0 && (
                <tr><td colSpan={(isPhone ? 2 : 3) + sheetCols.length}><EmptyState title="No vessels match" hint={activeFilters > 0 ? 'Try clearing the filters above.' : 'No vessels in this view.'} /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && groups.length === 0 && (
        <EmptyState title="No vessel groups found" hint={canWrite ? 'Use “+ Group” to start.' : undefined} />
      )}

      {histPopover}
      {tip.node}
      {photosFor && <ServicePhotos rowId={photosFor.id} name={photosFor.name} source={photosFor.source} canWrite={canWrite} initialFiles={photosFor.files} onClose={() => setPhotosFor(null)} />}
      {washFor != null && (() => {
        const e = washEntry(washFor);
        return (
          <WashDialog
            key={e.id}
            entry={e}
            editable={canWrite}
            today={todayIso}
            context={washContext(washFor)}
            onChange={(c) => setWash.mutate({ source: 'service', boatKey: e.boatKey, kind: 'service', boatName: e.name.slice(0, 160), boatModel: e.model.slice(0, 60), ...c })}
            onClose={() => setWashFor(null)}
          />
        );
      })()}
      {visitsFor != null && (() => {
        const info = visitsOf.get(visitsFor);
        const from = allVessels.find((x) => x.id === visitsFor);
        if (!info || !from) return null;
        // the next visit copies the boat's newest named visit (the one opened from may have no name yet)
        const base = [...info.all].reverse().map((x) => allVessels.find((r) => r.id === x.id)).find((r) => !!r && !!rawName(r).trim()) ?? from;
        const lines: VisitLine[] = info.all.map((x) => {
          const row = allVessels.find((r) => r.id === x.id);
          const roll = rollup(x.id);
          return { ...x, state: row && shipyardCol ? cellStr(row.cells[shipyardCol.id]).trim() : '', done: roll.done, works: roll.total };
        });
        return (
          <ServiceVisitsDialog
            boat={label(from)}
            visits={lines}
            current={visitsFor}
            currentSeason={currentSeason}
            canWrite={canWrite}
            adding={newVisitM.isPending}
            error={newVisitM.error}
            onShow={showVisit}
            onNewVisit={() => void newVisit(base)}
            onClose={() => setVisitsFor(null)}
          />
        );
      })()}
      {entryFor && (
        <VesselEntryForm
          vessel={entryFor.vessel}
          forms={formsOf.get(String(entryFor.vessel.rowId)) ?? []}
          formId={entryFor.formId}
          canWrite={canWrite}
          onClose={() => setEntryFor(null)}
        />
      )}

      {/* last, so a question asked from a dialog (New visit from the visits list) shows above it */}
      {confirmNode}
      <ActionBar show={selectMode && view === 'board'} message={selected.size ? `${selected.size} selected` : 'Tap the vessels to print'}>
        <button className="btn btn-ghost" onClick={() => { setSelecting(false); setSelected(new Set()); }}>Cancel</button>
        <button className="btn btn-primary" disabled={!selected.size} onClick={printSelected}><IconPrinter size={14} /> Print{selected.size ? ` (${selected.size})` : ''}</button>
      </ActionBar>
    </>
  );
}

// ── a growing multi-line text editor (Notes and other free-text columns) ─────────
// rows={1}: it starts the height of a field and grows with its lines (CSS field-sizing, or the height is
// fitted here where that isn't supported). Writes on blur only when the text really changed.
function GrowText({ value, disabled, title, onCommit }: { value: string; disabled: boolean; title: string; onCommit: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const fit = () => {
    const el = ref.current;
    if (!el || GROW_NATIVE) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  };
  useLayoutEffect(fit, []);
  return (
    <textarea
      ref={ref}
      rows={1}
      className="field svc-grow"
      defaultValue={value}
      disabled={disabled}
      title={title}
      onInput={fit}
      onBlur={(e) => { if (!sameText(e.target.value, value)) onCommit(e.target.value); }}
    />
  );
}

// ── a compact multi-picklist (tag) editor for a single cell ───────────────────
function TagCell({ value, options, disabled, onChange }: { value: string; options: string[]; disabled: boolean; onChange: (vals: string[]) => void }) {
  const tags = value ? value.split(/,\s*/).filter(Boolean) : [];
  const [adding, setAdding] = useState(false);
  const remaining = options.filter((o) => !tags.includes(o));
  return (
    <span className="grp-chips">
      {tags.map((t) => (
        <span key={t} className="grp-chip grp-chip-edit">
          {t}
          {!disabled && <button type="button" className="grp-chip-x" title="Remove" aria-label={`Remove ${t}`} onClick={() => onChange(tags.filter((x) => x !== t))}>×</button>}
        </span>
      ))}
      {!disabled && (adding && remaining.length ? (
        <select className="field" autoFocus value="" aria-label="Add tag" onChange={(e) => { if (e.target.value) onChange([...tags, e.target.value]); setAdding(false); }} onBlur={() => setAdding(false)} style={{ width: 'auto' }}>
          <option value="" />
          {remaining.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (!disabled && remaining.length > 0 && <button type="button" className="grp-chip grp-chip-add" title="Add tag" aria-label="Add tag" onClick={() => setAdding(true)}>＋</button>))}
    </span>
  );
}
